<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { eqsApi, type IssueEditRights } from '@/api/eqs';
import { changeDialogTitle, createChangeViaStepForm } from '@/composables/useChangeChecklist';
import { useIssueInlineUpdate } from '@/composables/useIssueInlineUpdate';
import { useSession } from '@/auth/session';
import ExportDataButton from '@/components/ExportDataButton.vue';
import PageState from '@/components/PageState.vue';
import ApprovalRouteDiagram from '@/components/ApprovalRouteDiagram.vue';
import ControlPlanTable from '@/components/ControlPlanTable.vue';
import DeliverablePairingPanel from '@/components/DeliverablePairingPanel.vue';
import DeliverableBoard from '@/components/DeliverableBoard.vue';
import PfmeaBatchImport from '@/components/PfmeaBatchImport.vue';
import FmeaTree from '@/components/FmeaTree.vue';
import ApprovalDecisionDialog from '@/components/ApprovalDecisionDialog.vue';
import IssueAssistDialog from '@/components/IssueAssistDialog.vue';
import ProjectIssueList from '@/components/ProjectIssueList.vue';
import ProjectTeamRelationship from '@/components/ProjectTeamRelationship.vue';
import TeamMemberHandoffModal from '@/components/TeamMemberHandoffModal.vue';
import RecordListDashboard from '@/components/RecordListDashboard.vue';
import IssueDrawerAssist from '@/components/IssueDrawerAssist.vue';
import ChangeLedgerTable from '@/components/ChangeLedgerTable.vue';
import ChangeStepBar from '@/components/ChangeStepBar.vue';
import RecordDrawer from '@/components/RecordDrawer.vue';
import type { DrawerChildList, DrawerStatusAction } from '@/components/record-drawer';
import RecordFormDialog from '@/components/RecordFormDialog.vue';
import SpecialCharacteristics from '@/components/SpecialCharacteristics.vue';
import StatusChip from '@/components/StatusChip.vue';
import { modules, projectSectionByKey, projectSectionFields } from '@/data/modules';
import OrgTreePicker from '@/components/OrgTreePicker.vue';
import PersonPicker from '@/components/PersonPicker.vue';
import { personLabel } from '@/utils/person';
import { notify } from '@/services/notice';
import type {
  Deliverable,
  ModuleField,
  DeliverableStatus,
  ModuleRecord,
  Project,
} from '@/types/domain';
import { errorMessage, formatCell, formatDate, formatResponsibility, statusText } from '@/utils/format';
import {
  buildDeliverableWritePayload,
  deliverableStatusActions,
  optionalPositiveInteger,
  type WorkflowAction,
} from '@/utils/project-workflow';
import { ISSUE_CLOSING_STATUSES } from '@/utils/issue-status';
import { buildExportFilename } from '@/utils/csv-export';
import { matchesDrill as matchesDashboardDrill, type DashboardLabels, type DrillFilter } from '@/utils/module-dashboard';
import { recordExportColumns, recordExportRows } from '@/utils/data-export';
import { confirmAction } from '@/services/confirm';
import { useProjectPlanWrite } from '@/utils/project-write';
import { useFormOptions } from '@/composables/useFormOptions';

const props = defineProps<{
  project: Project;
  section: string;
  title: string;
  summary?: unknown;
  portfolio?: unknown;
  detail?: unknown;
}>();
/*
  变更的「下一步 / 生成执行清单 / 催办措施」原来挂在这里（ChangeDrawerAssist）。
  0824 全部并进抽屉底下的 ChangeStepBar：「下一步」变成每一步自带的 hint，
  生成清单和催办落到第 4 步（清单本来就是那一步的任务）。
  一件事只留一个入口——两个入口的结果是人在两边各做一半。
*/





const route = useRoute();
const router = useRouter();
const { canWrite, canCloseIssues, user: sessionUser } = useSession();
/** 交付物清单里「我负责的」按它判；测试环境的会话桩没有 user，要能容忍 */
const viewer = computed(() => {
  const current = sessionUser?.value;
  return current ? { id: current.id, name: current.displayName } : null;
});
const items = ref<ModuleRecord[]>([]);
const allDeliverables = ref<ModuleRecord[]>([]);
const loading = ref(true);
const error = ref('');
const sourceWarning = ref('');
const issueAssigneeNames = ref<string[]>([]);
/** 本项目团队里绑了账号的成员（问题责任人选择器用） */
const issueAssigneeAccounts = ref<Array<{ userId: number; name: string; roleTitle: string | null }>>([]);
/**
 * 本项目 BOM 里的零件（0822 Tony 第 11 条）。
 * 零件号手打太容易打错，而且打错了就和 BOM 对不上、后面所有按零件号的统计都串。
 * 选号之后自动带出零件名称——名称本来就是跟着号走的，不该让人再抄一遍。
 */
const issueParts = ref<Array<{ number: string; name: string; customerNumber: string }>>([]);
/**
 * 每条问题「我能改什么」（0823 Tony 拍板的分层）。
 * 只用来灰掉格子；真正的拦截在后端 module-tools 的两条写入路由上。
 */
const issueEditRights = ref<Record<string, IssueEditRights>>({});
const selected = ref<ModuleRecord | null>(null);

/* 抽屉骨架（附件/历史/子条目/状态推进）现在是共享组件，两边同一份 */
const drawer = ref<InstanceType<typeof RecordDrawer> | null>(null);

/**
 * 看板下沉：开交付物和变更两个子页。
 *
 * 变更（1027 字典另票）：模块中心那套维度本来就是照库里真实取值挑的（类型 / 来源 / 牵头人 / 积压），
 * 唯一跨项目的「按关联项目」在项目页按作用域剔掉；类型柱子和模块中心借同一份字典翻中文。
 *
 * 其余子页先不给——DFMEA/PFMEA/控制计划在单个项目里通常只有个位数条，
 * 三根一样长的柱子比没有图更糟；团队走的是 hr 那套「邮箱可用性/账号形态」维度，
 * 那是给管理员看全公司账号质量的，放进十几个人的项目团队页毫无意义。
 * 要开哪个子页，就先给它挑真拿得到的维度，别顺手复用一份口径不对的配置。
 */
const dashboardModuleKey = computed(() => {
  if (isDeliverables.value) return 'deliverables';
  if (isChange.value) return 'changes';
  return '';
});
/*
  交付物页的看板默认收起：五张图占掉一整屏，清单被顶到下面，
  人打开这一页是来传文件、提交审批的，不是来看图的（0904 Tony）。
  统计数字改由清单顶上的筛选按钮带着；要看图点「查看看板」。
  1027（审查 P2-10）起不分子页一律默认收起，和模块中心同一个口径：执行页先给清单。
*/
const dashboardOpen = ref(false);
const dashboardFilter = ref<DrillFilter | null>(null);
const { fieldOptions, loadFormOptions, withStandardOptions } = useFormOptions();
/**
 * 变更类型 ID → 中文名，和模块中心（ModuleWorkbenchView）同一份写法：
 * 别名表 changeTypeAliases（同名重复导入的每个 ID）垫底，下拉那份 changeType 后写。
 * 只给看板和下钻用；表单下拉仍走 withStandardOptions，只吃 changeType。
 */
const changeTypeLabels = computed<Record<string, string>>(() =>
  Object.fromEntries([
    ...(fieldOptions.value.changeTypeAliases ?? []),
    ...(fieldOptions.value.changeType ?? []),
  ].map((item) => [String(item.value), item.label])));
/** 画图（RecordListDashboard）和下钻（visibleItems）同一份；项目页没有「按关联项目」，只借类型 */
const dashboardLabels = computed<DashboardLabels>(() => ({ changeTypes: changeTypeLabels.value }));
/** 图按本页全量画，列表按下钻结果显示——点一次就再也回不去是最气人的 */
const visibleItems = computed(() => items.value.filter((item) =>
  matchesDashboardDrill(dashboardModuleKey.value, item, dashboardFilter.value, dashboardLabels.value)));
/*
  这个组件在子页之间是复用的（交付物 → 变更不重建），下钻条件得自己丢：
  变更也有了看板之后，交付物上点的「按负责人 / 按状态」会原样筛到变更台账上。换项目同理。
*/
watch([() => props.section, () => props.project.id], () => { dashboardFilter.value = null; });
/*
  类型字典比列表晚到时（load() 开头字典和列表是并行取的），之前按「未映射类型（原值）」
  点下的柱子会和翻好的中文名对不上、把台账筛空——丢掉，和模块中心同一个口径。
*/
watch(dashboardLabels, () => {
  if (dashboardFilter.value?.kind === 'changetype') dashboardFilter.value = null;
});

/** 5Why / 历史案例要读写抽屉里的活表单，包成两个回调交给 IssueDrawerAssist */
function drawerValues(): Record<string, string> {
  return detailDialog.value?.getValues() ?? {};
}
function patchDrawerValues(patch: Record<string, string>): void {
  detailDialog.value?.setValues(patch);
}

/**
 * 名字对不上账号时不硬塞：写错一个字，待办就发给别人。
 */
function onApproverUnresolved(text: string): void {
  notify('error', '这个名字对不上账号', `「${text}」在通讯录里找不到唯一的人，请从下拉建议里选。`);
}

/** 审批人：默认走审批流，需要时才指定一个人（试用反馈 0814） */
const approverMode = ref<'workflow' | 'manual'>('workflow');
const pickedApproverId = ref<number | null>(null);
const approverNameInput = ref('');
const sourceIndex = ref(0);
const statusChanging = ref(false);
const binding = computed(() => projectSectionByKey(props.section));
const isDeliverables = computed(() => props.section === 'deliverables');
const isQualityDocument = computed(() =>
  ['dfmea', 'pfmea', 'control-plans'].includes(props.section));
const isIssueOrChange = computed(() =>
  ['issues', 'changes'].includes(props.section));
const isIssue = computed(() => props.section === 'issues');
const isChange = computed(() => props.section === 'changes');

/**
 * 打开的这条问题，「状态 / 实际完成时间」这一档我够不够格改。
 *
 * 这是 0823 分层里的第二档——责任人刻意不在里面：自己填完成时间、自己改状态，
 * 等于自己判自己合格。清单上灰的是状态那一格，抽屉里要挡的是状态按钮和完成日期，
 * 少挡一处，责任人点下去就是一个必然失败的按钮。
 *
 * 关闭也归这一档（0928 Tony：「我需要提出人和项目经理可以关闭」）：
 * 原来另有一道「只有质量能关」，项目经理和提出人都关不了，现在撤掉了。
 *
 * 权限表拿不到时按「够格」处理：后端仍然会拦并说清谁能改，
 * 而按钮凭空消失又没有任何解释，人只会认为系统坏了。
 */
const issueStatusTierAllowed = computed(() => {
  if (!isIssue.value || !selected.value) return true;
  const rights = issueEditRights.value[String(selected.value.id)];
  return rights ? rights.canEditStatus : true;
});

const isTeam = computed(() => props.section === 'team');
const isProjectFile = computed(() => props.section === 'files');

/**
 * 审计里记的 entity_type，用来查这条记录的改动史。
 * 对不上的子页不显示历史入口——宁可没有，也不要给一个永远查不到东西的按钮。
 */
const historyEntityType = computed<string | null>(() => ({
  deliverables: 'deliverable',
  issues: 'issue',
  changes: 'change',
  team: 'project-member',
  /*
    0824 补齐（Tony「把缺的补齐」）：这三个子页一直没有历史入口，
    而 PFMEA 在库里已经攒了 66 条改动记录——查的人只能跑去「操作留痕」页大海捞针。
    entityType 取自后端 modules.service.ts 的 ResourceConfig，
    id 空间和写审计时用的完全一致（都是各自文档表的行 id）。
    dfmea-document / control-plan 目前 0 条（还没人在这两处改过东西），
    补了暂时是空历史——但空历史和"没有入口"是两回事，前者会随着人开始用而长出来。
  */
  dfmea: 'dfmea-document',
  pfmea: 'pfmea-document',
  'control-plans': 'control-plan',
  /*
    files 这一行**是死代码**：/projects/:id/files 的路由组件是 ProjectFilesView，
    根本不走这个视图（router/index.ts:81）。留着会让人以为文件页有历史入口。
    文件的版本历史另有去处，见 ProjectFilesExplorer 里的版本弹窗。
  */
}[props.section] ?? null));
const supportsRecordFiles = computed(() =>
  isQualityDocument.value || isIssueOrChange.value || isProjectFile.value);
const requiresFileOnCreate = computed(() =>
  isDeliverables.value || isQualityDocument.value || isProjectFile.value);
const deliverableActions = computed(() => {
  if (!isDeliverables.value || !selected.value) return [];
  return deliverableStatusActions(
    String(selected.value.status || 'pending') as DeliverableStatus,
  );
});
const canSubmitDeliverable = computed(() =>
  deliverableActions.value.some((action) => action.submit));

/**
 * 状态推进里**不属于审批**的那几个动作。
 *
 * 审批中的时候，「批准 / 驳回」被摘掉：那两个走的是直接改状态那条路，
 * 一道权限判据都没有，点下去会把剩下几级审批整个跳过（后端 0904 也堵上了）。
 * 同一件事只留一个入口——要批就在上面的审批区里批。
 * 「取消」留着：交上去之后想撤回是正当需求，和跳级批准不是一回事。
 */
/*
  交付物「取消」只给本项目的项目经理（0930 Tony）。取消了它，这条任务标完成就不再要它的文件，
  是「完成要有证据」那道门的出口。判据和改计划同一条（planWritable），后端 deliverable-cancel-access.ts 拦。
  别人看得到这个按钮但点不了，下面一句话说清找谁——藏起来的话，人不知道还有这条路。
*/
const canCancelDeliverable = useProjectPlanWrite(computed(() => props.project));
function deliverableActionLocked(action: { value: string }): boolean {
  return action.value === 'cancelled' && !canCancelDeliverable.value;
}
const statusOnlyActions = computed(() => {
  if (!selected.value) return [];
  if (selected.value.approvalStatus !== 'pending') return deliverableActions.value;
  return deliverableActions.value.filter(
    (action) => action.value !== 'approved' && action.value !== 'rejected');
});

/** 正文的下载地址；没有可下载的版本就返回 null，不给点开是 404 的链接 */
const deliverableFileHref = computed(() => {
  const record = selected.value;
  if (!record?.documentId || !record.documentVersionId) return null;
  return eqsApi.fileDownloadUrl(
    Number(record.documentId), Number(record.documentVersionId));
});

const deliverableFileInput = ref<HTMLInputElement | null>(null);
const deliverableUploading = ref(false);
const pendingDeliverableSelectionId = ref<string | number | null>(null);
/*
  ?deliverableId=N：从文件页「打开交付物 →」跳过来时直接选中那一条（P11 第 2 步）。
  复用 pendingDeliverableSelectionId 的定位机制——load() 完成后优先选它。
*/
watch(
  () => route.query.deliverableId,
  (raw) => {
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (value) pendingDeliverableSelectionId.value = String(value);
  },
  { immediate: true },
);
// 项目任务跟踪回源：只定位本项目实际加载的问题，沿用原抽屉和权限。
let focusedIssueKey = '';
function focusRequestedIssue(): void {
  if (!isIssue.value || loading.value) return;
  const raw = Array.isArray(route.query.issueId) ? route.query.issueId[0] : route.query.issueId;
  const id = Number(raw);
  if (!Number.isSafeInteger(id) || id <= 0) { focusedIssueKey = ''; return; }
  const key = `${props.project.id}:${id}`;
  if (key === focusedIssueKey) return;
  const target = items.value.find(item => Number(item.id) === id);
  if (target) { focusedIssueKey = key; openDetailedRecord(target); }
}
watch(() => route.query.issueId, focusRequestedIssue);
const focusedPlanNodeId = computed(() => {
  if (!isDeliverables.value) return null;
  const raw = Array.isArray(route.query.nodeId) ? route.query.nodeId[0] : route.query.nodeId;
  const parsed = Number(raw);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
});
const focusedPlanTaskName = computed(() => {
  const raw = Array.isArray(route.query.taskName) ? route.query.taskName[0] : route.query.taskName;
  return typeof raw === 'string' ? raw.trim() : '';
});
const deliverableContextActive = computed(() => focusedPlanNodeId.value !== null);
/** 首页「我负责的交付物」点过来带的筛选：?quick=mine / decide / no-file … */
const deliverableInitialQuick = computed(() => {
  const raw = Array.isArray(route.query.quick) ? route.query.quick[0] : route.query.quick;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
});
/*
  PFMEA 的三个子标签（0910 Tony:「给 PFMEA 加子标签：结构 / 核对 / 导入」）。

  这一页原来把四件事竖着堆在一起——贯通核对、整簿导入、记录列表、结构树。
  它们回答的不是同一个问题：核对是「IATF 那条链通不通」，导入是建记录的工具，
  结构树才是每天要动的地方（SN26003 那份 4984 条目）。

  存在 URL 上（`?tab=`）而不是组件内部：刷新、发链接给人、从通知点进来
  都要落到同一个子页。默认「结构」——干活的那个。
*/
const PFMEA_TABS = [
  { key: 'structure', label: '结构' },
  { key: 'check', label: '核对' },
  { key: 'import', label: '导入' },
] as const;
type PfmeaTab = typeof PFMEA_TABS[number]['key'];

const isPfmea = computed(() => props.section === 'pfmea');
const pfmeaTab = computed<PfmeaTab>(() => {
  const raw = Array.isArray(route.query.tab) ? route.query.tab[0] : route.query.tab;
  const hit = PFMEA_TABS.find((entry) => entry.key === raw);
  return hit ? hit.key : 'structure';
});
/*
  核对挪进子标签之后，缺口数必须挂到标签上——不然就是把问题藏起来了：
  原来那张卡在页面最上面，红字扑面而来；现在不点「核对」根本不知道有问题。
  数字由 SpecialCharacteristics 加载完回报（口径只有它一份）。
*/
const pfmeaGapCount = ref<number | null>(null);
/*
  父组件直接取一次，不去挂一份隐藏的 SpecialCharacteristics 来「顺便」拿这个数：
  那样会把整套核对表格渲染出来只为读一个长度，将来谁看见都要愣一下。
  人在「核对」页时组件自己也会 emit 过来，两边都更新同一个 ref。
*/
async function loadPfmeaGapCount(): Promise<void> {
  if (props.section !== 'pfmea') return;
  try {
    const payload = await eqsApi.specialCharacteristics(props.project.id);
    pfmeaGapCount.value = payload.gaps?.length ?? 0;
  } catch {
    // 取不到就不标：宁可不显示，也不显示一个错的数
    pfmeaGapCount.value = null;
  }
}
async function selectPfmeaTab(tab: PfmeaTab): Promise<void> {
  const query = { ...route.query };
  if (tab === 'structure') delete query.tab; else query.tab = tab;
  await router.replace({ query });
}

/** Gate 提醒点过来带的阶段：?phase=P05，直接落到那一段的交付物上 */
const deliverableInitialPhase = computed(() => {
  const raw = Array.isArray(route.query.phase) ? route.query.phase[0] : route.query.phase;
  const code = typeof raw === 'string' ? raw.trim().toUpperCase() : '';
  return /^P0[1-6]$/u.test(code) ? code : null;
});

/** 交付物必须先挂文件才能提交审批——空记录提交上去审批人什么都看不到 */
async function uploadDeliverableFile() {
  const file = deliverableFileInput.value?.files?.[0];
  if (!selected.value || !file) {
    notify('error', '请先选择文件', '请选择要作为交付物正文的文件。');
    return;
  }
  deliverableUploading.value = true;
  try {
    const form = new FormData();
    form.append('file', file);
    await eqsApi.attachDeliverableFile(props.project.id, selected.value.id, form);
    notify('success', '交付物文件已上传', file.name);
    if (deliverableFileInput.value) deliverableFileInput.value.value = '';
    await load();
  } catch (caught) {
    notify('error', '交付物文件上传失败', errorMessage(caught));
  } finally {
    deliverableUploading.value = false;
  }
}

/** 审批决定改用对话框：意见要进审计记录，原生 prompt 无法校验也无法说明后果 */
const decisionKind = ref<'approved' | 'rejected' | null>(null);

function askDecision(decision: 'approved' | 'rejected') {
  decisionKind.value = decision;
}

async function submitDecision(comment: string) {
  if (!selected.value || !decisionKind.value) return;
  const decision = decisionKind.value;
  statusChanging.value = true;
  try {
    await eqsApi.decideProjectDeliverable(props.project.id, selected.value.id, decision, comment);
    notify('success', decision === 'approved' ? '已通过' : '已退回', formatCell(selected.value.title));
    decisionKind.value = null;
    await load();
  } catch (caught) {
    notify('error', '审批操作失败', errorMessage(caught));
  } finally {
    statusChanging.value = false;
  }
}

const labels = computed(() => {
  if (props.section === 'deliverables') return { entity: '交付物', primary: '交付物名称', code: '交付物编码' };
  if (props.section === 'dfmea') return { entity: 'DFMEA', primary: '分析对象', code: 'DFMEA 编号' };
  if (props.section === 'pfmea') return { entity: 'PFMEA', primary: '过程名称', code: 'PFMEA 编号' };
  if (props.section === 'control-plans') return { entity: '控制计划', primary: '控制计划名称', code: '控制计划编号' };
  if (props.section === 'issues') return { entity: '问题 / 8D', primary: '问题标题', code: '问题编号' };
  if (props.section === 'changes') return { entity: '工程变更', primary: '变更主题', code: '变更编号' };
  if (props.section === 'team') return { entity: '团队成员', primary: '成员姓名', code: '项目角色' };
  return { entity: '项目文件', primary: '文件名称', code: '文件编号' };
});

/**
 * 空状态说明。
 * 生产上多数模块记录还没有绑定 project_id（DFMEA 4 条 0 绑定、问题 7 条 0 绑定、DMS 401 条 0 绑定），
 * 所以项目子页看起来是空的——原因不是"没数据"，而是"没关联到本项目"。
 * 空状态必须说清这一点并给出两条出路，否则工程师会直接判定系统没东西。
 */
/**
 * 详细录入弹窗。
 *
 * 原先的常驻通用表单只有 11 个输入框，而问题管理定义了 23 个字段、变更管理 19 个、
 * 文件管理 15 个——「几行输入说不清楚一件事」正是这么来的。
 * 这里直接复用模块中心已有的完整字段定义，铺成弹窗多列布局。
 */
const detailOpen = ref(false);
const detailSaving = ref(false);

const detailModule = computed(() => {
  const source = binding.value?.sources[sourceIndex.value] ?? binding.value?.sources[0];
  if (!source) return null;
  return modules.find((item) => item.module === source.module && item.resource === source.resource)
    ?? modules.find((item) => item.module === source.module)
    ?? null;
});
/** 公司通讯录，供团队成员的「选择成员」下拉使用 */
const people = ref<Array<{ id: number; displayName: string; orgName: string | null; email: string | null }>>([]);
/** 项目角色选项：以审批流里的角色为准，审批派不到人的问题从这里治 */
const roles = ref<Array<{ role: string; inApproval: boolean; memberCount: number }>>([]);

/**
 * 抽屉里的「选东西」选项。
 *
 * 交付物挂节点、挂文件、选责任人，控制计划挂 PFMEA——原来都要人手填内部数字 ID
 * （「填写项目计划中的节点内部 ID」），没人知道自己要填几号，和团队页
 * 改造前是同一个毛病（试用反馈 8/13）。这里按需加载成可搜索下拉。
 */
const planNodeOptions = ref<Array<{ id: number; label: string }>>([]);
const documentOptions = ref<Array<{ id: number; label: string }>>([]);
const pfmeaOptions = ref<Array<{ id: number; label: string }>>([]);

async function loadPickerOptions(): Promise<void> {
  if (isDeliverables.value && !planNodeOptions.value.length) {
    try {
      const plan = await eqsApi.projectPlan(props.project.id);
      planNodeOptions.value = (plan.tasks ?? [])
        .filter((task) => task.nodeType !== 'phase')
        .map((task) => ({ id: Number(task.id), label: task.name }));
    } catch { planNodeOptions.value = []; }
  }
  if ((isDeliverables.value || isQualityDocument.value) && !documentOptions.value.length) {
    try {
      const payload = await eqsApi.projectResourceRecords('documents', 'documents', props.project.id);
      documentOptions.value = (payload.items ?? []).map((item) => ({
        id: Number(item.id),
        label: `${item.code ? `${String(item.code)} · ` : ''}${String(item.title ?? '')}`,
      }));
    } catch { documentOptions.value = []; }
  }
  if (props.section === 'control-plans' && !pfmeaOptions.value.length) {
    try {
      const payload = await eqsApi.projectResourceRecords('pfmea-cp', 'documents', props.project.id);
      pfmeaOptions.value = (payload.items ?? []).map((item) => ({
        id: Number(item.id),
        label: `${item.code ? `${String(item.code)} · ` : ''}${String(item.title ?? '')}`,
      }));
    } catch { pfmeaOptions.value = []; }
  }
}

/** 把「填内部 ID」的字段换成下拉；取不到选项时保留原输入框，不把人堵死 */
function withOptions(
  field: ModuleField,
  options: Array<{ id: number; label: string }>,
  emptyHint: string,
): ModuleField {
  if (!options.length) return { ...field, type: undefined, help: emptyHint };
  return {
    ...field,
    type: 'select',
    options: options.map((option) => String(option.id)),
    optionLabels: Object.fromEntries(options.map((option) => [String(option.id), option.label])),
  };
}

async function loadRoles(): Promise<void> {
  if (!isTeam.value || roles.value.length) return;
  try {
    const payload = await eqsApi.projectRoles();
    roles.value = payload.roles as unknown as typeof roles.value;
  } catch {
    roles.value = [];
  }
}

const peopleError = ref(false);

/**
 * 通讯录取不到时不再无声退化。
 *
 * 之前失败就静默吞掉，「选择成员」退化成一个数字输入框——用户看到的是
 * 一个只能点出 -3 的框，四个字段全卡死（试用反馈 8/13，触发时机正撞上
 * 部署重启的几十秒窗口）。现在失败会提示、打开弹窗时会再试。
 */
async function loadPeople(): Promise<void> {
  if (!(isTeam.value || isDeliverables.value || isIssue.value) || people.value.length) return;
  try {
    const payload = await eqsApi.people();
    people.value = (payload.people ?? []) as unknown as typeof people.value;
    peopleError.value = !people.value.length;
  } catch {
    people.value = [];
    peopleError.value = true;
  }
}

/*
  这里原来有 openChangeId / ownedFields / changeFieldsMoved / detailCollapsedGroups
  一整套「摘格 / 收组」判定。0826 变更的通用字段区整个删了（和五步重复），
  这套判定没有服务对象了，一并拆掉；别的子页从来不折叠分组（靠「按需填」老约定）。
*/
const detailCollapsedGroups = computed<'all' | string[] | undefined>(() => undefined);

/** 这个子页的全部字段定义（不管界面上摘没摘）——导出台账要按这一份出列 */
const detailFieldsAll = computed<ModuleField[]>(() => {
  // 交付物与团队成员走项目专属接口，字段单独定义
  const own = projectSectionFields[props.section];
  if (own && isDeliverables.value) {
    return own.map((field) => {
      if (field.key === 'nodeId') {
        return withOptions(field, planNodeOptions.value, '计划节点加载中…没有可选项说明本项目还没排计划。');
      }
      if (field.key === 'ownerUserId') {
        return withOptions(
          field,
          people.value.map((person) => ({
            id: person.id,
            label: person.orgName ? `${person.displayName}（${person.orgName}）` : person.displayName,
          })),
          '通讯录加载中…如果一直没有可选项，关掉弹窗重开或刷新页面。',
        );
      }
      if (field.key === 'documentId') {
        return withOptions(field, documentOptions.value, '本项目还没有文件；先建记录，之后在右侧上传正文。');
      }
      return field;
    });
  }
  if (own && props.section === 'control-plans') {
    return own.map((field) => (field.key === 'parentId'
      ? withOptions(field, pfmeaOptions.value, '本项目还没有 PFMEA；先到 PFMEA 页建立或导入。')
      : field));
  }
  if (own) {
    if (props.section !== 'team') return own;
    return own.map((field) => {
      if (field.key === 'parentId') {
        if (!people.value.length) {
          // 还没加载出来时不给数字框：一个能点出 -3 的 spinner 比空下拉更误导
          return { ...field, type: undefined, help: '通讯录加载中…如果一直没有可选项，关掉弹窗重开或刷新页面。' };
        }
        return {
          ...field,
          type: 'select' as const,
          options: people.value.map((person) => String(person.id)),
          optionLabels: Object.fromEntries(people.value.map((person) => [
            String(person.id),
            person.orgName ? `${person.displayName}（${person.orgName}）` : person.displayName,
          ])),
        };
      }
      // 项目角色：审批按角色名精确匹配派人，自由文本写错一个括号就派不到，且不报错
      if (field.key === 'code' && roles.value.length) {
        return {
          ...field,
          type: 'select' as const,
          options: roles.value.map((item) => item.role),
          optionLabels: Object.fromEntries(roles.value.map((item) => [
            item.role,
            item.inApproval ? `${item.role} · 审批在用` : `${item.role} · 审批流未用到`,
          ])),
          help: '从审批流用到的角色里选；标「审批流未用到」的选了也收不到审批任务。',
        };
      }
      if (field.key === 'title') {
        return { ...field, help: '选人后自动带出，一般不用动；没有系统账号的外部人员才手工填。' };
      }
      if (field.key === 'ownerName') {
        return { ...field, help: '选人后从账号自动带出，比成员表里的旧邮箱准。' };
      }
      return field;
    });
  }
  const fields = detailModule.value?.fields ?? [];
  // 项目子页里 projectId 由上下文决定，不该让人手填
  const projectFields = fields.filter((field) => field.key !== 'projectId');
  /*
    变更类型 / 变更来源 / 发生阶段这些标准选项库，原先只在模块中心接了字典，
    项目页是自由文本框——而人实际就是在项目里填变更的
    （试用反馈 0819：「变更来源与类型无标准选择库，叫法可能复杂多样」）。
    库里 change-type 11 种、issue-stage 是 P01–P06，早就有，缺的只是这行接线。
  */
  const withLibrary = withStandardOptions(projectFields);
  if (isIssue.value) {
    return withLibrary.map((field) => {
      // 主入口：用户选择器双写（优先本项目有账号成员；通讯录作补充）
      if (field.key === 'ownerUserId') {
        const teamOpts = issueAssigneeAccounts.value.map((m) => ({
          id: m.userId,
          label: m.roleTitle ? `${m.name}（${m.roleTitle}）` : m.name,
        }));
        const teamIds = new Set(teamOpts.map((o) => o.id));
        const peopleOpts = people.value
          .filter((p) => !teamIds.has(p.id))
          .map((p) => ({
            id: p.id,
            label: p.orgName ? `${p.displayName}（${p.orgName}）` : p.displayName,
          }));
        const options = [...teamOpts, ...peopleOpts];
        return withOptions(
          field,
          options,
          options.length
            ? '优先本项目团队账号；也可选通讯录。选中后自动带出姓名。'
            : '团队与通讯录都还没读到——稍等或先到「团队」页加人。',
        );
      }
      if (field.key === 'ownerName') {
        return {
          ...field,
          help: '选人后自动带出。仅「无系统账号」时手填，并清空上方负责人账号。',
        };
      }
      /*
        提出者原来从本项目团队里选（0822 第 10 条）。0928 Tony：「提出人不需要选择，
        问题谁填写谁就是提出人」——提出人能关问题，选出来的话谁都能把自己选成提出人。
        字段定义里已经是只读（data/modules.ts），这里不再改成下拉。
      */
      // 0822 第 11 条：零件号从本项目 BOM 里选，选完自动带出零件名称
      if (field.key === 'partNumber' && issueParts.value.length) {
        return {
          ...field,
          type: 'select' as const,
          options: issueParts.value.map((part) => part.number),
          optionLabels: Object.fromEntries(issueParts.value.map((part) => [
            part.number,
            [part.number, part.name, part.customerNumber && `客户号 ${part.customerNumber}`]
              .filter(Boolean).join(' · '),
          ])),
          // BOM 里没有的零件也要能填：现场提问题时件还没进 BOM 是常事，
          // 卡住人比填错更让人放弃用系统（0822 Tony 拍板留这个口子）
          allowFreeText: true,
          help: '从本项目 BOM 里选（东禾QAD号），选完自动带出零件名称；BOM 里没有的直接填也行。',
        };
      }
      /*
        实际完成时间和状态是同一档（0823）。字段不藏起来——藏了就等于这条问题
        什么时候完成的没人看得见；但要在人动手之前说清楚这一格不归他填，
        而不是等他填完点保存、被后端退回来才知道。
      */
      if (field.key === 'completedAt' && !issueStatusTierAllowed.value) {
        return {
          ...field,
          help: '实际完成时间由质量、项目经理或问题提出者填——它是「我做完了」的书面结论；'
            + '你填了保存时会被退回，做完请他们确认。',
        };
      }
      return field;
    });
  }
  return withLibrary;
});

/*
  表单上真正显示的格子。

  变更（0826 Tony）：通用字段区**整个不渲染**——那五组和五步重复，删。
  新建、编辑都在五步里做（第 1 步表单建单，第 3/4 步带门禁字段）。
  导出走的是上面那份 detailFieldsAll：界面上不渲染不等于台账少列。
*/
const detailFields = computed<ModuleField[]>(() => {
  if (props.section === 'changes') return [];
  return detailFieldsAll.value;
});

const exportColumns = computed(() => recordExportColumns(
  [
    { key: 'code', label: labels.value.code },
    { key: 'title', label: labels.value.primary },
  ],
  detailFieldsAll.value,
  true,
));
const exportRows = computed(() => recordExportRows(items.value, exportColumns.value));
const exportFilename = computed(() =>
  buildExportFilename([props.project.projectNo, labels.value.entity]));
const issuesZipBusy = ref(false);
async function exportIssuesZip(): Promise<void> {
  if (!isIssue.value || issuesZipBusy.value) return;
  const ids = items.value.map((row) => Number(row.id)).filter((id) => Number.isFinite(id) && id > 0);
  if (!ids.length) {
    notify('error', '导不出', '当前没有可导出的问题');
    return;
  }
  issuesZipBusy.value = true;
  try {
    const response = await fetch(
      eqsApi.issuesExportWithAttachmentsUrl(ids, props.project.id),
      {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/zip, application/json' },
      },
    );
    if (!response.ok) {
      let message = `导出失败（HTTP ${response.status}）`;
      try {
        const body = await response.json() as { message?: unknown };
        if (body?.message) message = Array.isArray(body.message) ? body.message.join('；') : String(body.message);
      } catch { /* keep default */ }
      notify('error', '导出含图失败', message);
      return;
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const stamp = new Date().toISOString().slice(0, 10).replaceAll('-', '');
    link.download = `${props.project.projectNo}_问题_含图_${stamp}.zip`;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (error) {
    notify('error', '导出含图失败', error instanceof Error ? error.message : String(error));
  } finally {
    issuesZipBusy.value = false;
  }
}

/** 编辑时把既有记录摊平成表单初值：核心列 + normalized 里的扩展字段 */
/** AI 提议的表单预填值；只在「新建」时并入，人复核后保存 */
const aiPrefill = ref<Record<string, string> | null>(null);
const assistOpen = ref(false);

function adoptAssistProposal(fields: Record<string, string>): void {
  aiPrefill.value = fields;
  assistOpen.value = false;
  detailOpen.value = true;
}

/**
 * 表单字段名 → 接口返回的列名。
 *
 * 接口按数据库列名返回下划线写法（parent_id / owner_name），表单字段用驼峰。
 * 对不上的字段在编辑页就是一片空白——「编辑团队成员」里选择成员和邮箱都是空的，
 * 看着像数据丢了（试用反馈 8/13）。问题单的责任人、要求完成日期是同一个毛病。
 */
const RECORD_KEY_ALIAS: Readonly<Record<string, string>> = {
  projectId: 'project_id', parentId: 'parent_id', ownerName: 'owner_name', ownerUserId: 'owner_user_id',
  dueAt: 'due_at', stepCode: 'step_code', versionNo: 'version_no',
};

const detailInitial = computed<Record<string, string>>(() => {
  const record = selected.value;
  if (!record) {
    if (isIssue.value && aiPrefill.value) return aiPrefill.value;
    return isDeliverables.value && focusedPlanNodeId.value !== null
      ? {
          nodeId: String(focusedPlanNodeId.value),
          title: focusedPlanTaskName.value,
        }
      : {};
  }
  if (isDeliverables.value) {
    return {
      title: String(record.title ?? ''),
      code: String(record.code ?? ''),
      nodeId: record.nodeId === null || record.nodeId === undefined ? '' : String(record.nodeId),
      ownerUserId: record.ownerUserId === null || record.ownerUserId === undefined ? '' : String(record.ownerUserId),
      ownerName: String(record.ownerName ?? ''),
      dueDate: String(record.dueAt ?? '').slice(0, 10),
      documentId: record.documentId === null || record.documentId === undefined ? '' : String(record.documentId),
    };
  }
  const normalized = (record.normalized ?? {}) as Record<string, unknown>;
  const values: Record<string, string> = {};
  for (const field of detailFields.value) {
    const raw = record[field.key] ?? record[RECORD_KEY_ALIAS[field.key] ?? field.key] ?? normalized[field.key];
    if (raw === null || raw === undefined) continue;
    values[field.key] = field.type === 'date'
      ? String(raw).slice(0, 10)
      : String(raw);
  }
  return values;
});

const detailDialog = ref<InstanceType<typeof RecordFormDialog> | null>(null);
/** 交付物页的批量挂文件面板；默认收起，需要补历史证据时才展开 */
const pairingOpen = ref(false);

/* 行内直传、提交审批、通过·退回都在 DeliverableBoard 里（0904 起清单换成按阶段分组的版本） */

/**
 * 选了成员就把姓名和邮箱带出来。
 *
 * 成员表里的邮箱是导入时脱敏的占位符（r***@joysonquin.com），
 * 账号表里才是真的——所以以账号为准覆盖，而不是保留旧值。
 */
/**
 * 按部门树选人（试用反馈 0814）。
 *
 * 平铺的打字下拉留着——知道名字时它最快；树是给「只知道是质量部的谁」用的。
 * 两条路都落到同一个 parentId 上，选完照样自动带出姓名和邮箱。
 */
const orgTreeOpen = ref(false);

function pickFromOrgTree(userId: number | null): void {
  if (!userId) return;
  detailDialog.value?.setValues({ parentId: String(userId) });
  onFieldPicked('parentId', String(userId));
  orgTreeOpen.value = false;
}

function onFieldPicked(key: string, value: string): void {
  // 问题里选了零件号，把零件名称一起带出来（0822 第 11 条）——名称跟着号走，不该让人再抄
  if (isIssue.value && key === 'partNumber' && value) {
    const part = issueParts.value.find((item) => item.number === value);
    if (part?.name) detailDialog.value?.setValues({ partName: part.name });
    return;
  }
  // 交付物 / 问题：选了责任人账号 → 双写姓名
  if ((isDeliverables.value || isIssue.value) && key === 'ownerUserId') {
    if (!value) {
      // 清空账号：留给「无系统账号」手填姓名
      return;
    }
    const uid = Number(value);
    const fromTeam = issueAssigneeAccounts.value.find((m) => m.userId === uid);
    if (fromTeam) {
      detailDialog.value?.setValues({ ownerName: fromTeam.name });
      return;
    }
    const picked = people.value.find((person) => person.id === uid);
    if (picked) detailDialog.value?.setValues({ ownerName: picked.displayName });
    return;
  }
  if (!isTeam.value || key !== 'parentId' || !value) return;
  const picked = people.value.find((person) => person.id === Number(value));
  if (!picked) return;
  const patch: Record<string, string> = { title: picked.displayName };
  if (picked.email) patch.ownerName = picked.email;
  detailDialog.value?.setValues(patch);
}


/**
 * 子条目动作（8D 步骤 / 变更措施的「完成」）之前，先把抽屉表单存了。
 *
 * 试用反馈 0819：「填完日期等信息仍不能提交断点完成」。
 * 「断点实施」要求父变更的「实际断点」非空——人在表单里填了、没点保存，
 * 就去点措施的「完成」，后端读库当然是空的，于是报「实际断点还是空的」，
 * 而那个值明明就显示在屏幕上。不是后端判错，是中间少了一步保存。
 *
 * 只在真的有未保存改动时才打断；干净的表单直接放行，不给人加一次点击。
 */
async function saveBeforeChildAction(): Promise<boolean> {
  const dialog = detailDialog.value;
  if (!dialog?.isDirty()) return true;
  const confirmed = await confirmAction({
    title: '表单里有还没保存的修改',
    message: '子条目的状态直接写进库，但你刚填的内容还只在页面上。先保存再继续，否则后端看不到。',
    confirmLabel: '保存后继续',
  });
  if (!confirmed) return false;
  await saveDetail(dialog.getValues(), null);
  return true;
}

async function saveDetail(values: Record<string, string>, file: File | null) {
  if (isDeliverables.value) {
    detailSaving.value = true;
    try {
      const payload = buildDeliverableWritePayload({
        nodeId: values.nodeId ?? '',
        code: values.code ?? '',
        title: values.title ?? '',
        ownerUserId: values.ownerUserId ?? '',
        ownerName: values.ownerName ?? '',
        dueDate: values.dueDate ?? '',
        documentId: values.documentId ?? '',
      });
      if (selected.value) {
        await eqsApi.updateProjectDeliverable(props.project.id, selected.value.id, payload);
        if (file) {
          const form = new FormData();
          form.append('file', file);
          await eqsApi.attachDeliverableFile(props.project.id, selected.value.id, form);
        }
      } else {
        const saved = await eqsApi.createProjectDeliverable(props.project.id, payload);
        pendingDeliverableSelectionId.value = saved.id;
        if (file) {
          try {
            const form = new FormData();
            form.append('file', file);
            await eqsApi.attachDeliverableFile(props.project.id, saved.id, form);
          } catch (uploadError) {
            notify(
              'error',
              '交付物已创建，但文件上传失败',
              `${errorMessage(uploadError)}。交付物已保留并自动选中，可直接重新上传。`,
            );
            detailOpen.value = false;
            await load();
            return;
          }
        }
      }
      notify(
        'success',
        selected.value ? '已保存' : file ? '交付物和文件已创建' : '已创建',
        file ? `${file.name} 已关联到当前计划任务。` : '交付物已写入。',
      );
      detailOpen.value = false;
      await load();
    } catch (caught) {
      notify('error', '保存失败', errorMessage(caught));
    } finally {
      detailSaving.value = false;
    }
    return;
  }

  const source = selected.value
    ? { module: String(selected.value.__module), resource: String(selected.value.__resource) }
    : binding.value?.sources[sourceIndex.value];
  if (!source) {
    notify('error', '未配置写入资源', '该子页没有配置可写入的业务资源。');
    return;
  }
  // 与模块中心同一套拆分口径：核心列进顶层，其余进 normalized
  const coreKeys = new Set(['parentId', 'code', 'title', 'priority', 'ownerName', 'dueAt', 'revision']);
  const payload: Record<string, unknown> = {};
  const normalized: Record<string, unknown> = {};
  for (const field of detailFields.value) {
    const raw = (values[field.key] ?? '').trim();
    if (!raw) continue;
    const value: unknown = field.type === 'number' ? Number(raw) : raw;
    if (coreKeys.has(field.key)) payload[field.key] = value;
    else normalized[field.key] = value;
  }
  if (Object.keys(normalized).length) payload.normalized = normalized;
  if (payload.parentId !== undefined) payload.parentId = Number(payload.parentId);
  // 选了人就用通讯录里的姓名兜底，省得人再抄一遍、抄错了两边对不上。
  // 手工填了姓名（外部人员之类）就尊重手工值。
  if (isTeam.value && payload.parentId) {
    const picked = people.value.find((person) => person.id === Number(payload.parentId));
    if (picked && !payload.title) payload.title = picked.displayName;
  }
  if (typeof payload.dueAt === 'string' && payload.dueAt) {
    payload.dueAt = new Date(`${payload.dueAt}T23:59:59+08:00`).toISOString();
  }
  /*
    标题这一格被摘走时（变更的编辑态，标题归第 1 步表单）不能再拿它拦人：
    表单里根本没有这一格，却弹「请填写变更主题」，人只会看到一个点了没反应的保存按钮。
    没摘的时候照旧拦——按「表单上有没有这一格」判，不按模块名写死。
  */
  const titleOnForm = detailFields.value.some((field) => field.key === 'title');
  if (titleOnForm && !payload.title) {
    notify('error', '表单未完成', `请填写${labels.value.primary}。`);
    return;
  }

  detailSaving.value = true;
  try {
    // 改问题走带分层权限的路由；新建不受限（「问题谁都能提」的口径没变）
    const saved = selected.value
      ? (isIssue.value
        ? await eqsApi.updateIssueRecord(selected.value.id, payload)
        : await eqsApi.updateProjectResource(source.module, source.resource, selected.value.id, payload))
      : await eqsApi.createProjectResource(source.module, source.resource, props.project.id, payload);
    if (file && supportsRecordFiles.value) {
      try {
        await uploadRecordFile(saved, source, file);
      } catch (uploadError) {
        notify('error', '记录已保存，但文件上传失败', errorMessage(uploadError));
      }
    }
    notify('success', selected.value ? '已保存' : '已创建', `${labels.value.entity}已写入。`);
    detailOpen.value = false;
    await load();
  } catch (caught) {
    notify('error', '保存失败', errorMessage(caught));
  } finally {
    detailSaving.value = false;
  }
}

/**
 * 空状态时，去本项目的文件里找「看起来就是这个东西」的原始文件。
 *
 * 秦岭OP3 的 PFMEA 页显示「本项目暂无 PFMEA」，但项目文件里躺着
 * 「ONVO Qingling 项目PFMEA.xlsx」——对工程师来说这就是自相矛盾：
 * 我明明传过。页面说的是「没有结构化记录」，人理解成「系统里没有」。
 * 与其让人在死胡同里打转，不如把手上真有的东西指出来。
 */
const SECTION_FILE_HINTS: Readonly<Record<string, RegExp>> = {
  dfmea: /d-?fmea|设计失效/i,
  pfmea: /p-?fmea|过程失效/i,
  'control-plans': /控制计划|control[ ]*plan/i,
};

const relatedFiles = ref<ModuleRecord[]>([]);

async function scanRelatedFiles(): Promise<void> {
  relatedFiles.value = [];
  const pattern = SECTION_FILE_HINTS[props.section];
  if (!pattern || items.value.length) return;
  try {
    const payload = await eqsApi.projectResourceRecords('documents', 'documents', props.project.id);
    relatedFiles.value = payload.items
      .filter((item) => pattern.test(String(item.title ?? '')))
      .slice(0, 6);
  } catch {
    // 找线索是锦上添花，失败就安静退回原来的空状态
    relatedFiles.value = [];
  }
}

const emptyState = computed(() => {
  const map: Record<string, { hint: string; modulePath: string | null }> = {
    deliverables: {
      hint: '本项目尚未产生交付物。交付物通常由计划任务生成，也可以点击页面右上角按钮新建。',
      modulePath: null,
    },
    dfmea: {
      hint: '本项目还没有关联 DFMEA。已有的 DFMEA 记录如果没有绑定项目，不会出现在这里。',
      modulePath: '/modules/dfmea',
    },
    pfmea: {
      hint: '本项目还没有关联 PFMEA。已有的 PFMEA 记录如果没有绑定项目，不会出现在这里。',
      modulePath: '/modules/pfmea-cp',
    },
    'control-plans': {
      hint: '本项目还没有关联控制计划。已有的控制计划如果没有绑定项目，不会出现在这里。',
      modulePath: '/modules/pfmea-cp',
    },
    issues: {
      hint: '本项目暂无质量问题记录。已登记但未绑定项目的问题不会出现在这里。',
      modulePath: '/modules/issues',
    },
    changes: {
      hint: '本项目暂无工程变更。已登记但未绑定项目的变更不会出现在这里。',
      modulePath: '/modules/changes',
    },
    team: {
      // 0916：人力资源模块撤了（和用户管理是同一张表的两个前端），人员在用户管理里维护
      hint: '本项目还没有团队成员，请先在用户管理里确认账号存在，再加入项目。',
      modulePath: '/admin/users',
    },
  };
  return map[props.section] ?? {
    hint: '本项目还没有关联文件。文件管理中未绑定项目的文档不会出现在这里。',
    modulePath: '/modules/documents',
  };
});

/**
 * 项目工作区内的批量导入。
 * 复用模块中心已有的模板/预览/确认三步接口，但把 projectId 作为参数传给服务端：
 * 模板里不再出现「关联项目内部 ID」，导入的每一行自动绑定当前项目。
 * 目标是让工程师用 Excel 一次维护完，而不是在页面上一条条点。
 */
const IMPORTABLE_SECTIONS = ['dfmea', 'pfmea', 'control-plans', 'issues', 'changes', 'files', 'team'];
const importOpen = ref(false);
const importInput = ref<HTMLInputElement | null>(null);
const importUploading = ref(false);
const importConfirming = ref(false);
const importPreview = ref<{
  fileName: string; total: number; creates: number; updates: number;
  valid: number; invalid: number;
  rows: Array<{ row: number; recordId?: number; action: string; values: Record<string, unknown>; errors: string[] }>;
} | null>(null);

const importSource = computed(() => binding.value?.sources[sourceIndex.value] ?? binding.value?.sources[0]);
const importAvailable = computed(() =>
  IMPORTABLE_SECTIONS.includes(props.section) && Boolean(importSource.value));
const templateHref = computed(() => (importSource.value
  ? eqsApi.moduleTemplateUrl(importSource.value.module, importSource.value.resource, props.project.id)
  : '#'));

async function runImportPreview() {
  const file = importInput.value?.files?.[0];
  const source = importSource.value;
  if (!file || !source) {
    notify('error', '请先选择文件', '请选择按模板填写好的 CSV 文件。');
    return;
  }
  importUploading.value = true;
  importPreview.value = null;
  try {
    const form = new FormData();
    form.append('file', file);
    importPreview.value = await eqsApi.previewModuleImport(
      source.module, source.resource, form, props.project.id,
    );
    notify(
      importPreview.value.invalid ? 'error' : 'success',
      '导入预览完成（尚未写入）',
      `共 ${importPreview.value.total} 行，有效 ${importPreview.value.valid} 行，异常 ${importPreview.value.invalid} 行。`,
    );
  } catch (caught) {
    notify('error', '导入预览失败', errorMessage(caught));
  } finally {
    importUploading.value = false;
  }
}

async function confirmImport() {
  const source = importSource.value;
  if (!source || !importPreview.value || importPreview.value.invalid) return;
  if (!await confirmAction({ title: `确认写入 ${importPreview.value.total} 行？新增 ${importPreview.value.creates} 行、更新 ${importPreview.value.updates} 行，全部关联到当前项目。`, })) return;
  importConfirming.value = true;
  try {
    const rows = importPreview.value.rows.map((row) => ({
      ...row.values,
      ...(row.recordId ? { recordId: row.recordId } : {}),
    }));
    const result = await eqsApi.confirmModuleImport(
      source.module, source.resource, rows, props.project.id,
    );
    notify('success', '批量导入完成', `新增 ${result.created} 行、更新 ${result.updated} 行。`);
    importPreview.value = null;
    if (importInput.value) importInput.value.value = '';
    importOpen.value = false;
    await load();
  } catch (caught) {
    notify('error', '批量导入失败', errorMessage(caught));
  } finally {
    importConfirming.value = false;
  }
}

/**
 * 每类记录自己的下一步。
 *
 * 原来只看状态字串给动作，不看这是什么记录——团队成员和控制计划的状态
 * 都是 active，于是给成员也弹一个「停用」。成员不是设备，人走了就该移出项目，
 * 「停用」是把数据库字段名当业务话讲（试用反馈 8/13）。
 *
 * 分两类处理：
 * - 质量记录（问题/变更/FMEA/控制计划/文件）：不给删除，只能作废。IATF 要可追溯，
 *   删掉了客户审核交代不了。动作名说清后果。
 * - 关系型数据（团队成员）：真删除，带确认和审计。
 */
type StatusStep = { status: string; label: string; hint: string; danger?: boolean };

const SECTION_STATUS_FLOW: Readonly<Record<string, Readonly<Record<string, StatusStep>>>> = {
  issues: {
    open: { status: 'in_progress', label: '开始处置', hint: '标记为正在处理，责任人会出现在待办里。' },
    pending: { status: 'in_progress', label: '开始处理', hint: '标记为正在处理。' },
    in_progress: { status: 'closed', label: '关闭问题', hint: '确认措施有效、问题不再复现后再关闭。' },
    rejected: { status: 'in_progress', label: '重新处理', hint: '退回的问题重新进入处置。' },
  },
  /*
    变更只剩「退回修改」一条。

    提交评审 / 批准变更 / 开始实施 / 实施完成这四步撤了——推进现在只有一个入口：
    抽屉底下五步条的「推进到第 N 步」，它同时写 current_step 和 status
    （后端 STEP_ADVANCE_STATUS）。两套入口并存的下场是库里出现「已关闭却停在第 1 步」。

    退回不能撤：五步只前进不后退（后端 advance 明确不倒退），
    评审没过要打回去重填，只能走这里。作废在模块中心（那边留了「取消」）——
    这一层每个状态只给一个按钮，项目页历来也没给过变更「作废」。
  */
  changes: {
    rejected: {
      status: 'draft',
      label: '退回修改',
      hint: '回到草稿继续修改。往前推进走上面五步的「推进到第 N 步」，那里会一并改状态。',
    },
  },
  files: {
    draft: { status: 'submitted', label: '提交审批', hint: '提交后进入审批，审批通过才能发布。' },
    submitted: { status: 'approved', label: '批准', hint: '批准后可发布生效。' },
    approved: { status: 'active', label: '发布生效', hint: '发布后为现行版本，可被其他记录引用。' },
    active: { status: 'obsolete', label: '作废', hint: '作废后不再是现行版本，记录和历史仍然保留。', danger: true },
  },
};

/** 质量文档（FMEA / 控制计划）共用一套发布流转 */
for (const key of ['dfmea', 'pfmea', 'control-plans']) {
  (SECTION_STATUS_FLOW as Record<string, unknown>)[key] = SECTION_STATUS_FLOW.files;
}

const genericStatusAction = computed<StatusStep | null>(() => {
  if (!selected.value || isDeliverables.value || isTeam.value) return null;
  const flow = SECTION_STATUS_FLOW[props.section];
  if (!flow) return null;
  const step = flow[String(selected.value.status || '')] ?? null;
  /*
    责任人推得动中间态，判「完成 / 关闭 / 作废」不归他（0823 Tony 拍板）。

    这里按**目标状态**判，不按字段判：按字段的话责任人接到问题连
    「开始处置」都点不了——而那恰恰是现场最该由他自己做的动作。
  */
  if (step && ISSUE_CLOSING_STATUSES.includes(step.status) && !issueStatusTierAllowed.value) {
    return null;
  }
  return step;
});

/** 状态区没有按钮时给一行字，别让人对着空白发呆——空白只会变成一通电话 */
const closeBlockedHint = computed(() => {
  if (props.section !== 'issues' || !selected.value) return '';
  const step = SECTION_STATUS_FLOW.issues?.[String(selected.value.status || '')];
  // 责任人推到了终态那一步：说清楚到这为止他能做什么、再往前得谁来
  if (step && ISSUE_CLOSING_STATUSES.includes(step.status) && !issueStatusTierAllowed.value) {
    const rights = issueEditRights.value[String(selected.value.id)];
    return `判「问题结束」和填实际完成时间，由质量、项目经理或问题提出者`
      + `（${rights?.foundBy?.trim() || '未填写'}）来定。`
      + '你可以继续更新进展、根因和措施，做完了请他们确认。';
  }
  return '';
});

/**
 * 变更的状态区常常一个按钮都没有（推进类的都撤了），得说清楚下一步在哪。
 *
 * 不说的话 RecordDrawer 会兜底成「当前状态没有可执行的下一步」——
 * 而变更明明有下一步，只是搬到上面的五步里了，那句话在这里就是假话。
 */
const changeStatusEmptyHint = computed(() => {
  if (props.section !== 'changes' || !selected.value) return '';
  return '往前推进走上面五步的「推进到第 N 步」，推进会一并改状态。'
    + '这条单子建错了要作废，到「模块中心 → 工程变更」里点「取消」。';
});

/** 团队成员不做状态流转：人不在这个项目了就移出，留着一个「停用」的成员没有意义 */
const memberRemovable = computed(() => isTeam.value && selected.value !== null);
const handoffMember = ref<ModuleRecord | null>(null);
const handoffOpen = ref(false);


/* 确认弹窗由 RecordDrawer 统一问过了（confirmMessage 里写清后果），这里只管执行 */
async function removeMember(): Promise<void> {
  const record = selected.value;
  if (!record) return;
  handoffMember.value = record;
  handoffOpen.value = true;
}

function closeSectionHandoff(): void {
  handoffOpen.value = false;
  handoffMember.value = null;
}

async function onSectionHandoffRemoved(): Promise<void> {
  handoffOpen.value = false;
  handoffMember.value = null;
  detailOpen.value = false;
  await load();
}

/**
 * 责任人 / 提出者下拉的候选：本项目团队成员。
 *
 * 取不到和「取到 0 个」是两码事，都要说出来：
 * 空团队时下拉会静默退回成自由文本框，人看到的是「说好的下拉呢」，
 * 而真正的原因是这个项目压根没维护过团队（列表少东西比列表报错难发现得多）。
 */
async function loadIssueAssignees(): Promise<void> {
  try {
    const team = await eqsApi.projectResourceRecords('hr', 'project-members', props.project.id);
    const names: string[] = [];
    const accounts: Array<{ userId: number; name: string; roleTitle: string | null }> = [];
    for (const member of team.items) {
      const name = String(member.title || member.name || '').trim();
      if (name) names.push(name);
      // parentId 映射的是 user_id（modules.service hr/project-members）
      const uidRaw = member.parentId ?? member.parent_id ?? member.userId ?? member.user_id;
      const uid = uidRaw === null || uidRaw === undefined || uidRaw === '' ? null : Number(uidRaw);
      if (uid && Number.isInteger(uid) && uid > 0 && name) {
        accounts.push({
          userId: uid,
          name,
          roleTitle: String(member.code || member.role_title || '').trim() || null,
        });
      }
    }
    issueAssigneeNames.value = [...new Set(names)].sort();
    // 同 userId 去重，保留先见到的姓名
    const seen = new Set<number>();
    issueAssigneeAccounts.value = accounts.filter((a) => {
      if (seen.has(a.userId)) return false;
      seen.add(a.userId);
      return true;
    });
    if (!issueAssigneeNames.value.length) {
      addSourceWarning('本项目团队里还没有成员，责任人请用通讯录选账号，或手填「无系统账号」姓名——'
        + '先到「团队」页把人加进来更稳。');
    }
  } catch (teamError) {
    addSourceWarning(`责任人选项读取失败：${errorMessage(teamError)}`);
  }
}

/** 零件号下拉的候选：本项目 BOM。读不到不影响填问题，只是回到手打 */
async function loadIssueParts(): Promise<void> {
  try {
    const bom = await eqsApi.bom(props.project.id) as unknown as {
      nodes?: Array<{ partNumber?: string; partName?: string; customerPartNumber?: string | null }>;
    };
    /*
      值统一用**东禾QAD号**，不用「客户零件号优先」。

      原来那条口径在真库上站不住：X04C 411 行 BOM 里只有 1 行填了客户零件号，
      下拉里会出来 410 个内部号加 1 个客户号——同一张单子上两套编号体系混着，
      而且哪天有人把客户号补上，这个零件在下拉里的身份就悄悄换了，
      早先按内部号填的问题从此对不上。

      东禾QAD号是 411/411 都有的那个，也是 0821 Tony 定的「最终的名字」。
      客户零件号放进显示标签里——按客户号搜也搜得到，但存的是稳定的那个。
    */
    const seen = new Map<string, { name: string; customerNumber: string }>();
    for (const node of bom.nodes ?? []) {
      const number = String(node.partNumber ?? '').trim();
      if (!number || seen.has(number)) continue;
      seen.set(number, {
        name: String(node.partName ?? '').trim(),
        customerNumber: String(node.customerPartNumber ?? '').trim(),
      });
    }
    issueParts.value = [...seen].map(([number, meta]) => ({ number, ...meta }));
  } catch {
    issueParts.value = [];
  }
}

/** 每条问题我能改什么。取不到就当全放开——后端仍然会拦，见 ProjectIssueList.editable() */
async function loadIssueEditRights(): Promise<void> {
  try {
    issueEditRights.value = await eqsApi.issueEditRights(props.project.id);
  } catch {
    issueEditRights.value = {};
  }
}

function addSourceWarning(message: string): void {
  sourceWarning.value = [sourceWarning.value, message].filter(Boolean).join('；');
}

/**
 * 变更台账的扩展列 + 费用列可见性（0826 Tony：变更列表改台账样式，
 * 费用仅本项目项目经理和管理员可见）。取不到就当不可见、扩展列显示 ——
 * 台账主体（行）不靠它，别让一个辅助接口拖垮列表。
 */
const ledgerExtras = ref<Record<string, {
  attachmentCount: number; appliedAt: string | null; closedAt: string | null; costAllowed: boolean;
  /** 这一步做完了、等人推进（0828）；台账上标「待推进」 */
  stepReady?: boolean;
}>>({});
const changeCostVisible = ref(false);

/*
  这条变更挂了几份资料（0828 关闭确认要看）。
  复用台账扩展列已经查过的数——再单独查一次是白费；
  查不到就当 0，宁可提醒人去传，不装作有。
*/
const closureAttachmentCount = computed(() => {
  const id = selected.value?.id;
  return id === undefined ? 0 : (ledgerExtras.value[String(id)]?.attachmentCount ?? 0);
});
async function loadLedgerExtras(): Promise<void> {
  if (props.section !== 'changes') return;
  try {
    const payload = await eqsApi.changeLedgerExtras(props.project.id);
    changeCostVisible.value = Boolean(payload.costVisible);
    ledgerExtras.value = Object.fromEntries(payload.items.map((item) => [String(item.id), item]));
  } catch {
    changeCostVisible.value = false;
    ledgerExtras.value = {};
  }
}

async function load() {
  loading.value = true;
  error.value = '';
  sourceWarning.value = '';
  /*
    三个都要清。原来只清了 issueAssigneeNames——从 A 项目切到 B 项目、
    B 的团队接口又恰好报错时，零件号下拉里躺着的还是 A 项目的 BOM 零件号，
    人挑一个填进去，问题就挂到了别的项目的件上。
  */
  issueAssigneeNames.value = [];
  issueAssigneeAccounts.value = [];
  issueParts.value = [];
  issueEditRights.value = {};
  void loadPfmeaGapCount();
  void loadFormOptions();   // 变更类型/来源/发生阶段的标准选项库，取一次进程内共享
  void loadLedgerExtras();  // 变更台账的附件数/日期/费用可见性，取不到不挡列表
  try {
    if (isDeliverables.value) {
      const loaded = (await eqsApi.projectDeliverables(props.project.id)).map((item) => ({
        ...item,
        __sourceLabel: '项目交付物',
      })) as unknown as ModuleRecord[];
      allDeliverables.value = loaded;
      items.value = focusedPlanNodeId.value === null
        ? loaded
        : loaded.filter((item) => Number(item.nodeId) === focusedPlanNodeId.value);
      const preferredId = pendingDeliverableSelectionId.value ?? selected.value?.id ?? null;
      pendingDeliverableSelectionId.value = null;
      const target = items.value.find((item) =>
        preferredId !== null && String(item.id) === String(preferredId)) ?? items.value[0];
      if (target) {
        pick(target);
      } else {
        clearEditor();
      }
      return;
    }
    if (!binding.value) throw new Error(`未配置项目子页资源：${props.section}`);
    if (binding.value.unavailableReason) throw new Error(binding.value.unavailableReason);
    const results = await Promise.allSettled(binding.value.sources.map((source) =>
      eqsApi.projectResourceRecords(source.module, source.resource, props.project.id)));
    const loaded: ModuleRecord[] = [];
    const failures: string[] = [];
    results.forEach((result, index) => {
      const source = binding.value!.sources[index];
      if (result.status === 'fulfilled') {
        loaded.push(...result.value.items.map((item) => ({
          ...item,
          __module: source.module,
          __resource: source.resource,
          __sourceLabel: source.label,
        })));
      } else {
        failures.push(`${source.label}：${errorMessage(result.reason)}`);
      }
    });
    if (!loaded.length && failures.length === results.length) throw new Error(failures.join('；'));
    items.value = loaded;
    sourceWarning.value = failures.join('；');
    /*
     * 控制计划直接摊开第一份的正文。
     * 这一页的主角是控制计划本身，不是那张卡片；
     * 让人先点一下才看得到内容，就是「进来了却找不到控制计划」（试用反馈 8/12）。
     * 只认第一份，多份时点卡片切换。
     */
    if (props.section === 'control-plans' && loaded.length) {
      const keep = loaded.find((item) => String(item.id) === String(selected.value?.id ?? ''));
      selected.value = keep ?? loaded[0]!;
    }
    if (isIssue.value) {
      /*
        团队、BOM、编辑权限三份数据互相独立，串着取的话前面一个报错
        后面两个就整个不跑了——0822 那版就是把 BOM 嵌在团队的 try 里，
        团队接口一挂，零件号下拉连试都不试。并行取，各自兜各自的底。
      */
      await Promise.all([loadIssueAssignees(), loadIssueParts(), loadIssueEditRights()]);
    }
  } catch (caught) {
    items.value = [];
    error.value = errorMessage(caught);
  } finally {
    loading.value = false;
    focusRequestedIssue();
    void scanRelatedFiles();
  }
}

/*
  附件接口要的 module/resource **显式算出来传给抽屉**，不再依赖 record.__module。
  行内编辑、快速加行这些路径会用接口返回值替换列表里的对象——标签一丢，
  抽屉里就是「当前记录缺少文件资源标识」，上传和保存全卡住（0820 P8 截图）。
  选中记录自带标签就用它，没有就按本子页的资源配置兜底。
*/
const drawerSource = computed(() => ({
  module: String(selected.value?.__module ?? binding.value?.sources[sourceIndex.value]?.module ?? ''),
  resource: String(selected.value?.__resource ?? binding.value?.sources[sourceIndex.value]?.resource ?? ''),
}));

function pick(item: ModuleRecord) {
  selected.value = item;
  if (isDeliverables.value) {
    approverMode.value = 'workflow';
    pickedApproverId.value = null;
    approverNameInput.value = '';
    return;
  }
  // 附件清单由 RecordDrawer 自己按 record.id 拉，这里不再各拉一遍
  sourceIndex.value = Math.max(0, binding.value?.sources.findIndex((source) => source.module === item.__module && source.resource === item.__resource) ?? 0);
}

function clearEditor() {
  selected.value = null;
  approverMode.value = 'workflow';
  pickedApproverId.value = null;
  approverNameInput.value = '';
  sourceIndex.value = 0;
}

/**
 * 问题清单底部快速加一行。
 *
 * 只收「描述 / 责任人 / 等级 / 要求完成日期」四个字段就落库——
 * 现场记问题时手上就这些，为它开一次抽屉太重（试用反馈 0818）。
 * 编号由后端按 ISS 前缀自动生成，发现日期默认今天。
 */
async function quickCreateIssue(draft: Record<string, string>): Promise<void> {
  const source = binding.value?.sources[sourceIndex.value] ?? binding.value?.sources[0];
  if (!source) return;
  const payload: Record<string, unknown> = {
    title: draft.title,
    normalized: {
      description: draft.description || draft.title,
      foundAt: new Date().toISOString().slice(0, 10),
      ...(draft.severityGrade ? { severityGrade: draft.severityGrade } : {}),
    },
  };
  if (draft.ownerName) payload.ownerName = draft.ownerName;
  if (draft.dueAt) payload.dueAt = new Date(`${draft.dueAt}T23:59:59+08:00`).toISOString();
  try {
    await eqsApi.createProjectResource(source.module, source.resource, props.project.id, payload);
    notify(
      'success',
      '已记下这条问题',
      '分类、零件号、根因和措施还没填——点这一行可以补齐。',
    );
    await load();
  } catch (caught) {
    notify('error', '保存失败', errorMessage(caught));
  }
}

/*
  问题清单单元格就地改。写入路径和全局问题清单（/modules/issues）共用一份，
  见 composables/useIssueInlineUpdate——那边原先没接这个事件，改了不写库也不报错。
*/
const { inlineUpdateIssue } = useIssueInlineUpdate(items, load);

/** 升级 / 降级写完库后只换这一条，不整表重载（和就地编辑同一套做法） */
function replaceIssue(saved: ModuleRecord): void {
  const index = items.value.findIndex((row) => String(row.id) === String(saved.id));
  if (index >= 0) items.value[index] = saved;
  else void load();
}

/** 团队页的卡片区组件：右上角「添加成员」要调它的快捷弹窗 */
const teamView = ref<InstanceType<typeof ProjectTeamRelationship> | null>(null);

/**
 * 右上角的新建按钮。
 *
 * 团队页不走通用记录表单（0904 Tony：增减成员要更便捷）：
 * 加人只需要「谁 + 什么角色」，走卡片区的快捷弹窗；通用表单留给编辑。
 */
function startNew(): void {
  if (isTeam.value) {
    teamView.value?.openQuickAdd();
    return;
  }
  startNewRecord();
}

function startNewRecord() {
  clearEditor();
  // 手动新建不该带上上一次 AI 的提议
  aiPrefill.value = null;
  // 通讯录之前没取到（比如撞上部署重启）就再试一次，别让人对着数字框发呆
  if ((isTeam.value || isIssue.value || isDeliverables.value) && !people.value.length) {
    void loadPeople().then(() => {
      if (peopleError.value && (isTeam.value || isIssue.value)) {
        notify('error', '通讯录加载失败', '负责人/成员选择暂时没有可选项。稍等几秒关掉弹窗重开，仍不行请刷新页面。');
      }
    });
  }
  detailOpen.value = true;
}

/**
 * 点行 = 看，不是改。
 *
 * 控制计划和 PFMEA 的正文视图（AIAG 表格、结构树）本来就在页面上，
 * 但点一行弹出来的是编辑表单，正文被压在后面——人想看控制计划，
 * 先撞上一屏「控制计划编号 / 修订版 / 零件编号」（试用反馈 8/14）。
 * 有正文可看的子页：点行只选中并滚到正文；要改再点行上的「编辑」。
 */
const HAS_CONTENT_VIEW = new Set(['pfmea', 'control-plans']);
const contentAnchor = ref<HTMLElement | null>(null);

function openDetailedRecord(item: ModuleRecord) {
  pick(item);
  if (HAS_CONTENT_VIEW.has(props.section)) {
    void nextTick(() => {
      // 老浏览器和测试环境没有 scrollIntoView；滚不动不该让整个点击炸掉
      contentAnchor.value?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    });
    return;
  }
  detailOpen.value = true;
}

/**
 * 正文读不出来时的「回列表」（PFMEA 结构树 404「PFMEA 1157 不属于项目 1」那类）：
 * 清掉这条选中，把列表拉回视野另选一条。只滚动、不跳路由，人还在 /projects/:id/pfmea。
 */
const recordListAnchor = ref<HTMLElement | null>(null);

function backToRecordList() {
  clearEditor();
  void nextTick(() => {
    recordListAnchor.value?.scrollIntoView?.({ block: 'start' });
  });
}

/** 行上的「编辑」：明确要改才打开表单 */
function editRecord(item: ModuleRecord) {
  pick(item);
  detailOpen.value = true;
}

/** 催办：站内 + 邮件双通道提醒责任人；结果如实回显（送没送到、走了哪些通道） */
/**
 * 「转 8D / 查看 8D」之后把人带到能看能改的地方。
 *
 * 8D 的填写界面在问题模块页（/modules/issues 的「8D 报告」视图），
 * 项目页这边只有清单。原来转完只弹一句提示就没了下文——
 * 人不知道去哪看、去哪改（2026-08-18 Tony：「整个流程还不是很通」）。
 */
function openIssue8d(item: ModuleRecord): void {
  void router.push({ path: '/modules/issues', query: { view: '8d', issueId: String(item.id) } });
}

async function remindIssue(item: ModuleRecord): Promise<void> {
  try {
    const result = await eqsApi.remindModuleRecord(
      String(item.__module ?? 'issues'),
      String(item.__resource ?? 'issues'),
      item.id,
    );
    if (result.notified) {
      const channels = (result.channels ?? []).includes('email') ? '站内 + 邮件' : '站内（无可用邮箱，邮件未发）';
      notify('success', '催办已发出', `${result.ownerName} 会收到提醒（${channels}）。`);
    } else {
      notify('error', '催办没有发出', result.skipped ?? '未知原因');
    }
  } catch (caught) {
    notify('error', '催办失败', errorMessage(caught));
  }
}

async function clearPlanNodeFocus() {
  const query = { ...route.query };
  delete query.nodeId;
  delete query.taskName;
  await router.replace({ query });
}

async function uploadRecordFile(
  record: ModuleRecord,
  source: { module: string; resource: string },
  file: File,
) {
  const payload = new FormData();
  payload.append('file', file);
  // 只在「新建记录时顺手带上的那个文件」这条路上用；说明栏在抽屉里，新建时还没有
  if (isProjectFile.value) {
    await eqsApi.uploadFileVersion(record.id, payload);
    return;
  }
  await eqsApi.uploadModuleAttachment(source.module, source.resource, record.id, payload);
}

/**
 * 审批列的文案。
 *
 * 生产库里有 23 条交付物自身状态是 approved/rejected，approval_status 却是 not_submitted——
 * 界面上就成了「已批准 · 审批 未提交」这种自相矛盾的话，工程师根本没法判断到底批没批。
 *
 * 根因：这些记录全部来自 legacy_eqs_206，旧 EQS 有审批结论，
 * 新系统的审批实例表里却没有对应记录（全库审批实例只有 1 条）。
 *
 * 刻意不回填 approval_status 假装审批过——那是伪造审计痕迹。如实说明来源即可。
 */
function approvalLabel(item: ModuleRecord): string {
  const approval = String(item.approvalStatus ?? 'not_submitted');
  const own = String(item.status ?? '');
  if (approval === 'not_submitted' && (own === 'approved' || own === 'rejected')) {
    return '旧系统结论，无新审批记录';
  }
  return statusText(approval);
}

function itemDescription(item: ModuleRecord): string {
  if (isDeliverables.value) {
    // ownerName 从库里出来是 '["郑鹤峰"]' 这种 JSON 字符串，直接显示就是一脸括号引号
    const owner = formatResponsibility(item.ownerName);
    const dueDate = typeof item.dueAt === 'string' ? item.dueAt.slice(0, 10) : '未设置期限';
    return `${owner} · ${dueDate} · 审批 ${approvalLabel(item)}`;
  }
  const normalized = item.normalized && typeof item.normalized === 'object'
    ? item.normalized as Record<string, unknown>
    : {};
  const context = [
    item.code,
    item.revision,
    // 挂了 BOM 零件就直接显示零件号——不显示的话没人知道这条 PFMEA 对应哪个零件
    normalized.bom_part_number ? `零件 ${String(normalized.bom_part_number)}` : null,
    item.owner_name,
    typeof item.due_at === 'string' ? item.due_at.slice(0, 10) : null,
    normalized.description,
  ].filter((value) => value !== null && value !== undefined && String(value).trim());
  return context.length ? context.map(formatCell).join(' · ') : '暂无补充信息';
}

/**
 * 抽屉里能点的下一步。
 *
 * 团队成员没有状态流转，能做的只有「移出项目」；
 * 其余子页按 SECTION_STATUS_FLOW 给一步。交付物审批是另一套，走 #status 插槽。
 */
const drawerStatusActions = computed<DrawerStatusAction[]>((): DrawerStatusAction[] => {
  if (memberRemovable.value) {
    return [{
      value: 'remove-member',
      label: '移出项目',
      danger: true,
      hint: '人不在这个项目了就移出，不必保留一条停用的成员记录。改角色直接在上面改。',
      confirmMessage: '移出后他不再收到这个项目的待办和审批通知；本人账号和其他项目不受影响。需要时可以重新添加。',
    }];
  }
  const action = genericStatusAction.value;
  if (!action) return [];
  return [{
    value: action.status,
    label: action.label,
    danger: action.danger,
    hint: action.hint,
  }];
});

/* 作废这条变更（0828）：入口搬到第 5 步卡片里，和「中途关闭」并排 */
async function voidChange(): Promise<void> {
  if (!selected.value) return;
  const ok = await confirmAction({
    title: '作废这条变更',
    message: '整条撤掉，不再流转，台账上标「终止」。已执行过的动作要按程序 10.4 发反向变更恢复原状。',
  });
  if (!ok) return;
  await changeStatus({ value: 'cancelled', label: '取消' } as DrawerStatusAction);
}

async function changeStatus(action: DrawerStatusAction) {
  if (!selected.value) return;
  if (action.value === 'remove-member') {
    await removeMember();
    return;
  }
  try {
    // 抽屉里改状态同样过分层：责任人改不了状态（0823）
    if (isIssue.value) {
      await eqsApi.advanceIssueRecord(selected.value.id, action.value);
    } else {
      await eqsApi.advanceProjectResource(
        String(selected.value.__module),
        String(selected.value.__resource),
        selected.value.id,
        action.value,
      );
    }
    notify('success', '状态已更新', action.label);
    await load();
    resyncSelectedAfterReload();
    /*
      五步条只 watch changeId，同一条记录上点完状态按钮它不知道要重读——
      「退回修改」把单子打回草稿，五步条却还显示在原来那一步（0824）。
      换个 refreshKey 让它自己去后端问一遍。
    */
    recordRefreshTick.value += 1;
  } catch (caught) {
    notify('error', '状态更新失败', errorMessage(caught));
  }
}

/** 记录被外部动过的次数：状态按钮改完 +1，用来叫醒抽屉里按 id 缓存的块 */
const recordRefreshTick = ref(0);

/**
 * 列表重拉之后，把选中的这条也换成库里最新的——但**只在状态真的变了时换**。
 *
 * 换 selected 会让抽屉表单按新初值重建，人填了一半没保存的东西就没了；
 * 不换的话，五步推进改了状态、状态区还显示旧状态，按钮也是按旧状态算出来的。
 * 只在状态变了时换，两头的代价都最小。
 */
function resyncSelectedAfterReload(): void {
  const current = selected.value;
  if (!current) return;
  const fresh = items.value.find((item) => String(item.id) === String(current.id));
  if (fresh && String(fresh.status ?? '') !== String(current.status ?? '')) pick(fresh);
}

/** 五步里动了东西（推进 / 生成清单 / 勾措施）：列表和记录都要跟着变，推进现在也会改状态 */
async function onChangeStepChanged(): Promise<void> {
  await load();
  resyncSelectedAfterReload();
}

/**
 * 新建工程变更（0826）：新建屏 = 第 1 步表单，值和凭证文件从五步条 emit 上来。
 * 走项目页自己的建单接口（createProjectResource，项目从路由带，自动挂当前项目）；
 * 传附件、提交第 1 步的链路和模块中心共用 composable。建完不关抽屉，切到编辑态接着走。
 */
const changeCreating = ref(false);
async function createChangeFromStep(payload: {
  values: Record<string, string>; file: File | null; customerImages?: File[];
}): Promise<void> {
  if (changeCreating.value) return;
  const source = binding.value?.sources[sourceIndex.value];
  if (!source) {
    notify('error', '未配置写入资源', '该子页没有配置可写入的业务资源。');
    return;
  }
  changeCreating.value = true;
  try {
    const record = await createChangeViaStepForm(
      (body) => eqsApi.createProjectResource(source.module, source.resource, props.project.id, body),
      payload,
    );
    if (!record) return;
    await load();
    const fresh = items.value.find((item) => String(item.id) === String(record.id)) ?? record;
    openDetailedRecord(fresh as ModuleRecord);
  } finally {
    changeCreating.value = false;
  }
}

/**
 * 抽屉抬头。
 *
 * 变更单独带上编号和标题：这两格摘进第 1 步表单之后，抬头是唯一还能看出
 * 「我在看哪条」的地方，而变更编号是程序文件规定的唯一追溯码。
 * 别的模块编号标题还在表单第一格摆着，不跟着改（改了就动到别人的界面）。
 */
const detailDialogTitle = computed(() => {
  if (!selected.value) return `新建${labels.value.entity}`;
  const base = `编辑${labels.value.entity}`;
  return props.section === 'changes' ? changeDialogTitle(base, selected.value) : base;
});

/** 抽屉的文件区形态：项目文件走 DMS 版本，其余业务记录挂附件 */
  /*
    0920 Tony:「业务附件啥的其实没啥用，可以去掉这一块」。

    变更的文件现在全挂在五步里各自那一格下面：第 1 步「变更内容附件」是 catch-all
    （没归属的也在那儿，能传能下能撤），客户要求原文、可行性方案、客户批准 / 告知记录
    各自独立成段。抽屉底下这一块就是同一批文件再列一遍。

    摘之前先查了它挡不挡门禁（0828 的规矩：藏起来又挡路是最坏的组合）——
    第 1 步「至少 1 个附件」读的是同一张表，而上传 / 下载 / 撤下在「变更内容附件」
    那一栏全都有：门还在，入口也还在，只是不再重复摆一份。
  */
const drawerFileMode = computed<'versions' | 'attachments' | 'none'>(() => {
  if (!supportsRecordFiles.value) return 'none';
  if (isProjectFile.value) return 'versions';
  // 新建变更时仍然要传附件（第 1 步的门禁），所以只摘抽屉这一块，supportsRecordFiles 不动
  return isChange.value ? 'none' : 'attachments';
});

/** 抽屉里的子条目：问题挂 8D 步骤，变更挂执行清单。文案与模块中心一致 */
const drawerChildren = computed<DrawerChildList | null>((): DrawerChildList | null => {
  if (isIssue.value) {
    return {
      module: 'issues',
      resource: 'steps',
      title: '8D 步骤（D0–D8）',
      emptyHint: '这条问题还没有 8D 步骤。需要走八步法就点上面「一键建好 D0–D8」；单独补一步用右上「＋ 添加」。',
      extraFields: { remarks: '备注', category: '分类' },
      stepOptions: ['D0', 'D1', 'D2', 'D3', 'D4', 'D5', 'D6', 'D7', 'D8'],
    };
  }
  /*
    变更的执行清单不在这里挂了：它就是五步里第 4、5 步的任务本身，
    抽屉底下再挂一份等于同一批措施显示两遍，勾在哪一份里全看人当时滚到哪。
    连同 extraFields（变更内容/功能影响/外观影响）一起搬进 ChangeStepBar 的第 4 步。
    8D 那支不动。
  */
  return null;
});

async function submitDeliverable() {
  if (!selected.value) return;
  // 审批人留空时由后端按「任务审批流步骤角色 × 项目团队成员」解析，
  // 手填只作为兜底，不再强制。
  // 选了「按审批流」就一律不带审批人，后端按角色解析
  const approverUserId = approverMode.value === 'manual' ? pickedApproverId.value : null;
  const approverName = people.value.find((person) => person.id === approverUserId)?.displayName;
  if (!await confirmAction({ title: approverUserId
      ? `确认提交“${formatCell(selected.value.title)}”，交给 ${approverName ?? `用户 ${approverUserId}`} 审批？`
      : `确认提交“${formatCell(selected.value.title)}”？系统将按审批流把待办派给对应角色的项目成员。`, })) return;
  statusChanging.value = true;
  try {
    await eqsApi.submitProjectDeliverable(
      props.project.id,
      selected.value.id,
      approverUserId ?? undefined,
      approverMode.value === 'manual'
        ? (people.value.find((person) => person.id === pickedApproverId.value)?.displayName ?? approverNameInput.value)
        : approverNameInput.value,
    );
    notify('success', '已提交审批', approverUserId
      ? `审批工作项已分配给 ${approverName ?? `用户 ${approverUserId}`}。`
      : '已按审批流派发给对应角色的项目成员。');
    await load();
  } catch (caught) {
    notify('error', '提交审批失败', errorMessage(caught));
  } finally {
    statusChanging.value = false;
  }
}

async function advanceDeliverable(status: DeliverableStatus, label: string) {
  if (!selected.value || !await confirmAction({ title: `确认执行“${label}”？` })) return;
  statusChanging.value = true;
  try {
    await eqsApi.advanceProjectDeliverableStatus(
      props.project.id,
      selected.value.id,
      status,
    );
    notify('success', '交付物状态已更新', label);
    await load();
    clearEditor();
  } catch (caught) {
    notify('error', '状态更新失败', errorMessage(caught));
  } finally {
    statusChanging.value = false;
  }
}

function runDeliverableAction(action: WorkflowAction<DeliverableStatus>) {
  if (action.submit) {
    void submitDeliverable();
    return;
  }
  void advanceDeliverable(action.value, action.label);
}

watch(() => [
  props.section,
  props.project.id,
  route.query.nodeId,
  route.query.taskName,
], () => {
  void load();
  // 从别的子页切到团队页时通讯录和角色表还是空的（两个加载都只在对应子页才真的取）
  void loadPeople();
  void loadRoles();
});
onMounted(() => { void load(); void loadPeople(); void loadRoles(); void loadPickerOptions(); });
</script>

<template>
  <header class="subpage-context">
    <p>
      <span>PROJECT / {{ section.toUpperCase() }}</span>
      <template v-if="isTeam">本项目的团队名单。审批、评审和待办都按这里的角色和账号找人。</template>
      <template v-else>记录与当前项目内部 ID {{ project.id }} 绑定。</template>
    </p>
    <div class="section-header-actions">
      <ExportDataButton
        :columns="exportColumns"
        :rows="exportRows"
        :filename="exportFilename"
        :label="`导出${labels.entity}`"
      />
      <button
        v-if="isIssue"
        class="button"
        type="button"
        :disabled="!items.length || issuesZipBusy"
        :title="items.length ? `把本页问题打包成 ZIP（清单 + 图片），共 ${items.length} 条` : '暂无可导出数据'"
        data-export-issues-zip
        @click="exportIssuesZip"
      >{{ issuesZipBusy ? '正在打包…' : '导出含图 ZIP' }}<span v-if="items.length">（{{ items.length }}）</span></button>
      <button v-if="importAvailable" class="button" type="button" @click="importOpen = !importOpen">
        {{ importOpen ? '收起批量导入' : '批量导入 / 更新' }}
      </button>
      <!-- 交付物挂文件是这个系统当前最大的缺口（2029/2036 没挂），给它一个专门入口 -->
      <button v-if="isDeliverables" class="button" type="button" @click="pairingOpen = !pairingOpen">
        {{ pairingOpen ? '收起批量挂文件' : '批量挂文件' }}
      </button>
      <button
        class="button button-primary"
        type="button"
        @click="startNew"
      >＋ {{ isTeam ? '添加成员' : `${requiresFileOnCreate ? '上传' : '新建'}${labels.entity}` }}</button>
    </div>
  </header>

  <DeliverablePairingPanel
    v-if="isDeliverables && pairingOpen"
    :project-id="project.id"
    @paired="load"
  />

  <aside v-if="isDeliverables && deliverableContextActive" class="plan-node-context">
    <div>
      <b>正在处理计划任务：{{ focusedPlanTaskName || `节点 ${focusedPlanNodeId}` }}</b>
      <span>仅显示该任务的 {{ items.length }} 个交付物；上传文件后可继续提交审批。</span>
    </div>
    <button class="inline-button" type="button" @click="clearPlanNodeFocus">
      查看全部 {{ allDeliverables.length }} 个交付物
    </button>
  </aside>

  <section v-if="importAvailable && importOpen" class="import-panel">
    <div class="import-steps">
      <b>用 Excel 一次维护完，不用一条条填</b>
      <span>
        ① 下载模板（已按本项目生成，<strong>无需填写项目 ID</strong>）→ ② Excel 填好另存为 CSV →
        ③ 上传预览 → ④ 确认写入。<br>
        留空「记录ID」为新增；填写「记录ID」为更新既有记录。导入的每一行都会自动关联本项目。
      </span>
    </div>
    <div class="import-actions">
      <a class="button" :href="templateHref" download>下载 {{ labels.entity }} 模板</a>
      <input ref="importInput" type="file" accept=".csv,text/csv" @change="importPreview = null">
      <button class="button" type="button" :disabled="importUploading" @click="runImportPreview">
        {{ importUploading ? '正在解析…' : '上传并预览' }}
      </button>
    </div>
    <div v-if="importPreview" class="import-result" :class="{ 'has-error': importPreview.invalid > 0 }">
      <b>{{ importPreview.fileName }}</b>
      <p>共 {{ importPreview.total }} 行 · 新增 {{ importPreview.creates }} · 更新 {{ importPreview.updates }} · 异常 {{ importPreview.invalid }}（尚未写入）</p>
      <ul v-if="importPreview.invalid">
        <li v-for="row in importPreview.rows.filter((item) => item.errors.length).slice(0, 8)" :key="row.row">
          第 {{ row.row }} 行：{{ row.errors.join('；') }}
        </li>
      </ul>
      <button
        class="button button-primary"
        type="button"
        :disabled="importPreview.invalid > 0 || importConfirming"
        @click="confirmImport"
      >{{ importConfirming ? '正在写入…' : importPreview.invalid ? '请先修正异常行' : `确认写入 ${importPreview.total} 行` }}</button>
    </div>
  </section>

  <ProjectTeamRelationship
    v-if="isTeam && !loading && !error"
    ref="teamView"
    :project-id="project.id"
    :project-manager="project.projectManager"
    :members="items"
    :people="people"
    :roles="roles"
    :writable="canWrite"
    @edit="openDetailedRecord"
    @changed="load"
  />

    <TeamMemberHandoffModal
      v-if="handoffMember"
      :open="handoffOpen"
      :project-id="project.id"
      :member="handoffMember"
      :people="people"
      :team-members="items"
      @close="closeSectionHandoff"
      @removed="onSectionHandoffRemoved"
    />
  <ApprovalDecisionDialog
    :decision="decisionKind"
    :subject="selected ? String(selected.title ?? '') : ''"
    :saving="statusChanging"
    @close="decisionKind = null"
    @submit="submitDecision"
  />

  <RecordFormDialog
    ref="detailDialog"
    :open="detailOpen"
    @field-picked="onFieldPicked"
    :title="detailDialogTitle"
    :fields="detailFields"
    :initial="detailInitial"
    :saving="detailSaving"
    :submit-visible="section !== 'changes'"
    :file-enabled="!selected && section !== 'changes' && (supportsRecordFiles || isDeliverables)"
    :file-required="!selected && requiresFileOnCreate"
    :collapsed-groups="detailCollapsedGroups"
    @close="detailOpen = false; aiPrefill = null"
    @submit="saveDetail"
  >
    <!--
      五步框架排在最上面，0826 起也是变更**唯一**的填写口：通用字段区删了
      （和五步重复），新建、编辑都在五步里做——新建的凭证文件也在第 1 步表单里选。
    -->
    <template v-if="section === 'changes'" #before-form>
      <ChangeStepBar
        :change-id="selected ? Number(selected.id) : null"
        :record="selected"
        :people="issueAssigneeNames"
        :writable="canWrite"
        :refresh-key="recordRefreshTick"
        :before-child-action="saveBeforeChildAction"
        :creating="changeCreating"
        :attachment-count="closureAttachmentCount"
            :cost-visible="changeCostVisible"
        @changed="onChangeStepChanged"
          @void="voidChange"
        @create="createChangeFromStep"
      />
    </template>
    <template #operations>
      <!-- 团队成员：除了打字筛选，也能按部门树找人（试用反馈 0814） -->
      <section v-if="isTeam" class="dialog-operations org-picker-block">
        <span>ORG CHART</span>
        <h3>按部门找人</h3>
        <p class="operation-hint">
          知道名字就直接在上面的「选择成员」里打字；只知道部门时用这棵树。
        </p>
        <button class="button button-secondary" type="button" @click="orgTreeOpen = !orgTreeOpen">
          {{ orgTreeOpen ? '收起组织架构' : '打开组织架构' }}
        </button>
        <OrgTreePicker
          v-if="orgTreeOpen"
          :model-value="null"
          @update:model-value="pickFromOrgTree"
        />
      </section>

      <RecordDrawer
        ref="drawer"
        :record="selected"
        :file-mode="drawerFileMode"
        :file-label="isProjectFile ? 'VERSION CONTROL' : isQualityDocument ? 'PRIMARY FILE / HISTORY' : 'BUSINESS ATTACHMENTS'"
        :file-title="isProjectFile ? '项目文件版本' : isQualityDocument ? `${labels.entity}文件与更新历史` : '业务附件'"
        :module="drawerSource.module"
        :resource="drawerSource.resource"
        :history-entity-type="historyEntityType"
        :history-hint="isDeliverables ? '这条交付物是谁在什么时候改的。' : ''"
        :children="drawerChildren"
        :before-child-action="saveBeforeChildAction"
        :status-actions="drawerStatusActions"
        :status-title="isTeam ? '成员操作' : '这条记录的下一步'"
        :status-context="isTeam || !selected ? '' : `当前状态：${statusText(selected.status)}`"
        :status-empty-hint="closeBlockedHint || changeStatusEmptyHint"
        :status-busy="statusChanging"
        :writable="canWrite"
        readonly-hint="你的账号在这个项目里只有查看权限，改动请找项目经理。"
        @status="changeStatus"
        @files-changed="load"
      >
        <template #assist>
          <!-- 5Why/AI、一键铺 D0–D8：和模块中心挂的是同一个组件。
               变更的五步条不在这里了，搬到了字段之前的 before-form（0824 顺序调整） -->
          <IssueDrawerAssist
            v-if="isIssue && selected"
            :record="selected"
            :writable="canWrite"
            :get-values="drawerValues"
            :set-values="patchDrawerValues"
            @steps-changed="drawer?.reloadChildren()"
          />
        </template>

        <!-- 交付物是另一套：审批人选择 + 正文直传，只有项目页有 -->
        <template v-if="isDeliverables && selected" #status>
        <section class="status-actions">
          <span>APPROVAL / STATUS</span><h3>审批与状态推进</h3>
          <p class="status-context">当前：{{ statusText(selected.status) }} · 审批：{{ approvalLabel(selected) }}</p>
          <!--
            0904 Tony:「这个评审那边为什么没有自动评审流转的页面」。
            数据一直都有（审批实例记着走到第几级、每一级谁批的），只是这个抽屉
            没把它画出来——人只看到一行「等谁审批」，看不出整条链有几级、
            前面是谁放的行。计划节点那边早就有这张图了，这里补上。
            按**交付物**取而不是按节点：一个节点下可能挂好几条交付物。

            0911 起这是**唯一**一张带进度的流转图。阶段工作页原来也有一张，
            但那张按节点取实例（forNode 里 ORDER BY id DESC LIMIT 1），
            挑到的常常是同节点下别的交付物的进度，两边对不上——Tony 在
            0911 的问题梳理里点了这条，那张已经撤掉。
          -->
          <ApprovalRouteDiagram
            :key="`route-${selected.id}`"
            :project-id="project.id"
            :deliverable-id="selected.id"
            :writable="canWrite"
            @reassigned="load" />
          <!-- 改动史由 RecordDrawer 统一渲染在状态区下面，这里不再重复一份 -->
          <div class="deliverable-file">
            <b>交付物正文</b>
            <!--
              0904 Tony:「这个交付物已挂，但是在编辑里面还是看不到下载的地方」。
              原来只写一句"已上传"——文件在系统里，人却打不开。
            -->
            <p v-if="deliverableFileHref">
              <a class="deliverable-download" :href="deliverableFileHref" target="_blank" rel="noopener">
                下载 {{ selected.documentFileName || '交付物正文' }}
              </a>
              <small>可继续上传新版本，旧版本保留。</small>
            </p>
            <p v-else-if="selected.documentId" class="missing">
              记录里挂着文件，但取不到可下载的版本（文件本体可能没上传成功）——重传一次。
            </p>
            <p v-else class="missing">尚未上传文件 —— 上传后才能提交审批。</p>
            <div class="deliverable-file-actions">
              <input ref="deliverableFileInput" type="file" />
              <button class="button" type="button" :disabled="deliverableUploading" @click="uploadDeliverableFile">
                {{ deliverableUploading ? '上传中…' : selected.documentId ? '上传新版本' : '上传交付物文件' }}
              </button>
            </div>
          </div>
          <template v-if="canSubmitDeliverable">
            <!--
              原来这里是「审批人用户 ID」一个数字输入框——没人知道自己是几号，
              填错就把待办派给别人（试用反馈 0814，肖娟）。改成两选一：
              默认按审批流自动派发，需要指定时从通讯录选人。
            -->
            <fieldset class="approver-choice">
              <legend>谁来审批</legend>
              <label class="approver-radio">
                <input v-model="approverMode" type="radio" value="workflow" />
                <span><b>按审批流自动派发</b><small>系统按「任务审批流步骤角色 × 项目团队成员」找人，推荐</small></span>
              </label>
              <label class="approver-radio">
                <input v-model="approverMode" type="radio" value="manual" />
                <span><b>指定一个人</b><small>审批流找不到人、或这次要绕过时用</small></span>
              </label>
              <template v-if="approverMode === 'manual'">
                <PersonPicker
                  :people="people"
                  :model-value="pickedApproverId"
                  label="指定审批人"
                  list-id="deliverable-approver-options"
                  @update:model-value="pickedApproverId = $event"
                  @unresolved="onApproverUnresolved"
                />
                <datalist id="deliverable-approver-options">
                  <option v-for="person in people" :key="person.id" :value="personLabel(person)" />
                </datalist>
                <p v-if="!people.length" class="approver-hint">通讯录还没读到，稍等一下或刷新页面。</p>
              </template>
            </fieldset>
          </template>
          <!--
            审批按钮只给**轮到的那个人**。
            原来的判据是 approvalStatus === 'pending'，那说的是"这条在审批中"，
            不是"该你审"——于是所有人都看到「审批通过」，点下去吃一个
            「当前账号没有这条交付物的待审批任务」（0904 Tony:「没办法审批」）。
            canDecide 由后端算，和 decide() 里那条判据是同一条。
          -->
          <template v-if="selected.approvalStatus === 'pending'">
            <div v-if="selected.canDecide" class="button-row">
              <button class="button button-primary" type="button" :disabled="statusChanging" @click="askDecision('approved')">审批通过</button>
              <button class="button button-danger" type="button" :disabled="statusChanging" @click="askDecision('rejected')">退回</button>
            </div>
            <p v-else class="approval-waiting">
              <template v-if="selected.waitingFor">现在等 <b>{{ selected.waitingFor }}</b> 审批，还没轮到你。</template>
              <template v-else>正在审批中，但没查到当前该谁审——请项目经理检查这条的审批流是否配好了人。</template>
            </p>
          </template>
          <div v-if="statusOnlyActions.length" class="button-row">
            <button
              v-for="action in statusOnlyActions"
              :key="action.value"
              class="button"
              :class="action.danger ? 'button-danger' : action.submit ? 'button-primary' : 'button-secondary'"
              type="button"
              :disabled="statusChanging || deliverableActionLocked(action)"
              :title="deliverableActionLocked(action) ? '取消交付物只有本项目的项目经理能做' : undefined"
              @click="runDeliverableAction(action)"
            >{{ statusChanging ? '处理中…' : action.label }}</button>
          </div>
          <small
            v-if="statusOnlyActions.some((action) => deliverableActionLocked(action))"
            class="field-hint deliverable-cancel-hint"
          >取消交付物只有本项目的项目经理能做。不需要这份交付物，请找项目经理取消。</small>
          <small v-if="!statusOnlyActions.length && selected.approvalStatus !== 'pending'" class="field-error">当前状态没有可执行的下一步。</small>
        </section>
        </template>
      </RecordDrawer>
    </template>
  </RecordFormDialog>

  <!-- FMEA 结构树是 1298 条的层级数据，始终使用整行宽度展示。 -->
  <section v-if="isIssue" class="project-issue-page">
    <PageState
      v-if="loading"
      state="loading"
      title="正在读取项目问题清单"
      message="加载问题、责任人和处理状态。"
    />
    <PageState
      v-else-if="error"
      state="error"
      title="项目问题清单不可用"
      message="下方是接口返回的原因；重试仍失败请联系管理员。"
      :detail="error"
      @retry="load"
    />
    <template v-else>
      <aside v-if="sourceWarning" class="api-warning"><b>部分资源不可用</b><span>{{ sourceWarning }}</span></aside>
      <IssueAssistDialog
        :open="assistOpen"
        :project-id="project.id"
        @close="assistOpen = false"
        @adopt="adoptAssistProposal"
        @created="assistOpen = false; load()"
      />
      <ProjectIssueList
        :project-id="project.id"
        :items="items"
        :can-edit="canWrite"
        :can-close-issues="canCloseIssues"
        :edit-rights="issueEditRights"
        :owner-options="issueAssigneeNames"
        @edit="openDetailedRecord"
        @create="startNewRecord"
        @assist="assistOpen = true"
        @remind="remindIssue"
        @quick-create="quickCreateIssue"
        @inline-update="inlineUpdateIssue"
        @escalated="replaceIssue"
        @open8d="openIssue8d"
      />
    </template>
  </section>

  <!-- 团队页读完后只剩卡片区：下面这份通用清单和卡片是同一批人，重复（0904 Tony） -->
  <div
    v-if="!isIssue && !(isTeam && !loading && !error)"
    class="workspace-section-grid"
  >
    <section class="content-panel">
      <PageState v-if="loading" state="loading" :title="`正在读取${title}`" message="连接项目上下文接口。" />
      <PageState v-else-if="error" state="error" :title="`${title}接口不可用`" message="下方是接口返回的原因；重试仍失败请联系管理员。" :detail="error" @retry="load" />
      <div v-else ref="recordListAnchor" class="section-record-list">
        <!--
          PFMEA 的三个子标签。核对那个挂缺口数：挪进子页之后，
          不标出来就等于把问题藏起来（原来那张卡在最上面，红字扑面而来）。
        -->
        <nav v-if="isPfmea" class="pfmea-tabs" aria-label="PFMEA 子页">
          <button
            v-for="entry in PFMEA_TABS"
            :key="entry.key"
            type="button"
            :class="{ 'is-active': pfmeaTab === entry.key }"
            :aria-pressed="pfmeaTab === entry.key"
            @click="selectPfmeaTab(entry.key)"
          >
            {{ entry.label }}
            <i v-if="entry.key === 'check' && pfmeaGapCount" class="pfmea-tab-badge">{{ pfmeaGapCount }}</i>
          </button>
        </nav>

        <!-- 特殊特性贯通核对：PFMEA 走「核对」子页，控制计划页仍是横幅配角 -->
        <SpecialCharacteristics
          v-if="section === 'control-plans' || (isPfmea && pfmeaTab === 'check')"
          :project-id="project.id"
          :embedded="isPfmea"
          @loaded="pfmeaGapCount = $event"
        />
        <!-- 整簿导入：自己一页；项目一条 PFMEA 都没有时也要能用 -->
        <PfmeaBatchImport
          v-if="isPfmea && pfmeaTab === 'import'"
          :project-id="project.id"
          @imported="load"
        />
        <aside v-if="sourceWarning" class="api-warning"><b>部分资源不可用</b><span>{{ sourceWarning }}</span></aside>
        <!-- 看板：点柱子筛下面的清单，口径和画图共用 utils/module-dashboard -->
        <RecordListDashboard
          v-if="dashboardModuleKey && (!isPfmea || pfmeaTab === 'structure')"
          v-model="dashboardFilter"
          v-model:open="dashboardOpen"
          :module-key="dashboardModuleKey"
          :entity-name="labels.entity"
          :items="items"
          :filtered-count="visibleItems.length"
          scope="project"
          :min-items="8"
          :change-type-labels="changeTypeLabels"
        />
        <!--
          变更走台账样式（0826 Tony：照老 Excel 变更台账的列）。
          费用四列只有管理员和本项目项目经理看得到（后端同口径裁行）。
        -->
        <ChangeLedgerTable
          v-if="section === 'changes' && visibleItems.length"
          :items="visibleItems"
          :extras="ledgerExtras"
          :cost-visible="changeCostVisible"
          :selected-id="selected?.id ?? null"
          @edit="openDetailedRecord"
        />
<!--
          交付物走自己的清单（0904）：按阶段分组、按计划顺序排，
          传文件 / 提交审批 / 通过·退回都在行上；「详情」才进下面那个弹窗。
        -->
        <DeliverableBoard
          v-if="isDeliverables"
          :project-id="project.id"
          :items="visibleItems"
          :people="people"
          :writable="canWrite"
          :viewer="viewer"
          :selected-id="detailOpen && selected ? selected.id : null"
          :initial-quick="deliverableInitialQuick"
          :initial-phase="deliverableInitialPhase"
          @open="openDetailedRecord"
          @changed="load"
        />
        <!-- 行本身是可点区域（交付物不走这里，见上面的 DeliverableBoard） -->
        <div
          v-for="item in section === 'changes' || isDeliverables || (isPfmea && pfmeaTab !== 'structure')
            ? [] : visibleItems"
          :key="`${item.__module}-${item.id}`"
          class="record-row"
          :class="{ 'is-active': selected?.id === item.id && selected?.__module === item.__module }"
        >
          <button class="record-open" type="button" @click="openDetailedRecord(item)">
            <StatusChip :value="item.status" />
            <span><b>{{ formatCell(item.title || item.name || item.code) }}</b><small>{{ formatCell(item.__sourceLabel) }} · {{ itemDescription(item) }}</small></span>
            <i>{{ HAS_CONTENT_VIEW.has(section) ? '查看正文 →' : '编辑 →' }}</i>
          </button>
          <!-- 有正文视图的子页：编辑降为次要动作，单独一个入口 -->
          <button
            v-if="HAS_CONTENT_VIEW.has(section)"
            class="record-edit"
            type="button"
            :aria-label="`编辑 ${formatCell(item.title || item.code)}`"
            @click="editRecord(item)"
          >编辑</button>
        </div>
        <div v-if="!items.length" class="section-empty">
          <b>本项目暂无{{ labels.entity }}</b>
          <span>{{ emptyState.hint }}</span>
          <!-- 文件里有疑似的东西就直说，别让人以为系统里什么都没有 -->
          <div v-if="relatedFiles.length" class="section-empty-files">
            <b>不过本项目的「文件」里有这些，可能就是你要找的：</b>
            <ul>
              <li v-for="file in relatedFiles" :key="String(file.id)">{{ file.title }}</li>
            </ul>
            <RouterLink class="queue-action" :to="`/projects/${project.id}/files`">去文件页查看 →</RouterLink>
            <small>它们是上传的原始文件；要做贯通核对（特殊特性、与控制计划对照）仍需建成结构化记录。</small>
          </div>
          <div class="section-empty-actions">
            <RouterLink v-if="emptyState.modulePath" class="queue-action primary" :to="emptyState.modulePath">
              去模块中心查找并关联
            </RouterLink>
            <span class="section-empty-note">或点击页面右上角按钮新建</span>
          </div>
        </div>
        <!-- 下钻把这一页筛空了：说清是筛没的，不是本来就没有 -->
        <div v-else-if="!visibleItems.length && dashboardFilter && !isDeliverables" class="section-empty">
          <b>当前筛选下没有{{ labels.entity }}</b>
          <span>
            看板上点了「{{ dashboardFilter.value }}」，本页 {{ items.length }} 条里没有符合的。
            点上面提示条里的「清除」回到全部。
          </span>
        </div>

        <!-- 正文区放在列表下方：点行往下就是它的正文，不用回头往上找（试用反馈 8/14） -->
        <div
          v-if="HAS_CONTENT_VIEW.has(section) && selected && (!isPfmea || pfmeaTab === 'structure')"
          ref="contentAnchor"
          class="record-content"
        >
          <header class="record-content-head">
            <div>
              <b>{{ formatCell(selected.title || selected.code) }}</b>
              <small>上面列表里蓝色那条的正文；要改基本信息点该行右侧的「编辑」</small>
            </div>
            <button class="inline-button" type="button" @click="editRecord(selected)">编辑这条</button>
          </header>
          <!-- PFMEA 结构树：过程步骤→功能→失效→措施 -->
          <FmeaTree
            v-if="section === 'pfmea'"
            :project-id="project.id"
            :document-id="selected.id"
            @clear-selection="clearEditor"
            @back-to-list="backToRecordList"
          />
          <!-- 控制计划正文：不打开就只能看到一张卡片，等于「有控制计划但读不了」 -->
          <ControlPlanTable
            v-else-if="section === 'control-plans'"
            :control-plan="selected"
          />
        </div>
      </div>
    </section>
  </div>
</template>
